/**
 * A tela de conversa de WhatsApp (8.3, 8.4, 8.6, 11.12 e a quarta passada sobre o protótipo v2 —
 * FR-37, FR-38, FR-41, FR-71, FR-76, UX-DR7, UX-DR11).
 *
 * Duas colunas no desenho do WhatsApp Web (protótipo v2, `.whats`): a lista de conversas à
 * esquerda, com 340 px, e a conversa aberta ocupando o resto. O objetivo declarado do épico é não
 * precisar treinar ninguém para usar o que já sabe usar — **o Vendedor chega aqui sem passar por
 * Empresa ou Negócio antes**, e o que ele vê é o que já usa no celular.
 *
 * **A quarta passada (29/09/2026) trouxe a tela para o protótipo v2 e para o gesto do WhatsApp
 * Web**, depois de o Gustavo fotografar a produção lado a lado com o desenho:
 *
 * - a conversa ocupa a tela (a 11.12 a tinha embrulhado num `flex` sem `flex: 1`, e ela
 *   encolhia até o conteúdo), e o balão enviado fica **à direita** (o `row-reverse` com
 *   `justify-content: flex-end` o empurrava para a esquerda);
 * - a lista tem avatar, nome, as etiquetas da equipe ao lado do nome (13.6), a prévia e a hora; os filtros
 *   **Todas / A responder**; a busca "Pesquisar conversa" (desde a 13.21b o "começar" é o lápis ✎
 *   ao lado dela, "Iniciar nova conversa" — `IniciarNovaConversa.tsx`);
 * - desde o chamado de 21/10/2026, a linha da lista tem a **razão social embaixo do nome** quando
 *   o `cadastro` a entregou (pedido do Gustavo; o protótipo a põe dentro do nome), e a busca acha
 *   o telefone digitado com máscara, comparando dígitos pela cópia da chave do backend
 *   (`busca-por-telefone.ts`), e também a razão social exibida na linha;
 * - o cabeçalho do chat tem avatar, nome, etiquetas e contexto, e **sempre** uma ação: "Abrir
 *   negócio" quando há Negócio, e "+ Negócio para esta pessoa" quando não há (a Pessoa solta
 *   inclusive — é o primeiro contato virando Negócio, FR-3);
 * - o chat tem o fundo do WhatsApp, o separador de dia, balões brancos à esquerda e verdes à
 *   direita, hora em fonte mono e a marca de enviada; o gesto de reagir aparece ao passar o mouse
 *   (ou ao focar) sobre o balão, como no próprio WhatsApp;
 * - **soltar arquivos sobre a conversa** abre a prévia, e o clipe abre o menu de tipos (fotos e
 *   vídeos, documento, áudio). **Nada sobe sem passar pela prévia** (`AnexosParaEnviar`): a
 *   legenda é escrita olhando para o arquivo, e "Enviar" manda um a um;
 * - o nome do contato é o do cadastro, senão o que ele usa no WhatsApp (`nome_no_whatsapp`, novo
 *   no serviço), senão o número formatado — nunca mais o número cru de quem tem nome.
 *
 * **Os dados do contato (13.3, chamado de 21/10, P1)**: o nome do cabeçalho é um botão **dentro**
 * do heading (o heading continua com o nome por nome acessível) e abre o painel "Dados do contato"
 * (`DadosDoContato.tsx`); a linha da lista ganhou o menu de clique direito — e, no celular, o "⋯"
 * fora do botão dela — com o mesmo item, que abre a conversa **e** o painel. O clique simples na
 * linha continua só abrindo a conversa. O estado do painel mora aqui, guardado pelo `conversaId`:
 * trocar de conversa ou a conversa sair da lista o fecha (D-16), e o aviso do vínculo sobrevive no
 * topo da página quando a conversa vinculada sai da lista (FR-39). A partir de 1200 px o painel é a
 * terceira coluna; entre 768 e 1199 px, uma gaveta sobre o chat; no celular, a tela no lugar do
 * chat.
 *
 * **A tela compõe dois serviços, e só dois** (AD-25; o painel do contato lê também o `negocios`,
 * como o "Abrir negócio" já lia). O `comunicacao` devolve `pessoa_id`,
 * `empresa_id`, `estado_da_empresa` e `nome_no_whatsapp`; razão social e nome de contato do
 * cadastro quem resolve é este arquivo, chamando o `cadastro` em **lote** com o JWT de quem está
 * olhando. Resolvê-los do lado do serviço, com token de serviço, entregaria à tela um nome que a
 * policy do `cadastro` esconderia daquele usuário.
 *
 * **"Abrir negócio" (11.12)**: o `negocios` responde `GET /negocios?empresa_id=` (os em andamento
 * da Empresa, pela RLS de quem olha), e a conversa com Empresa conhecida pergunta **uma vez, pela
 * conversa aberta**: um Negócio abre o modal da 11.4 direto sobre esta tela, vários abrem a
 * escolha. A composição continua do SPA — o `comunicacao` não chama o `negocios`.
 *
 * **Da lista à resposta são dois cliques** (espinha): no computador, abrir uma conversa pela lista
 * põe o foco no compositor. **Só a pedido**: o carregamento da página não rouba o foco, e no
 * celular o teclado virtual não sobe por cima do chat que acabou de abrir. Citar mensagem (quote)
 * não existe aqui nem no serviço, de propósito (spec da 11.12).
 *
 * **"Desconectado" em vermelho, com o caminho para Integrações** (State Patterns da espinha): a
 * faixa aparece quando o próprio número está desconectado (e uma neutra quando está pareando), as
 * conversas continuam legíveis, e compositor, anexo, soltar e microfone ficam desabilitados
 * apontando a faixa como razão. A situação é relida a cada trinta segundos com a aba à vista.
 *
 * **A conversa aberta mora na URL, e não num `useState`.** Quem a resolve é a rota
 * (`rotas/whatsapp.tsx`), e este componente recebe a decisão pronta.
 *
 * **A paridade de celular é de árvore, e não de `@media`** (NFR-1). No celular a tela monta
 * **uma** coluna — a lista ou o chat —, abre na lista, e o toque numa conversa abre o chat em tela
 * cheia com um "Conversas" que volta. **Todas** as funções existem nas duas larguras.
 *
 * **O relógio, e onde ele para.** A mensagem do cliente chega pelo webhook, por fora da tela; a
 * releitura é a de `api.ts:INTERVALO_DA_CONVERSA`, e ela para quando a aba perde o foco.
 *
 * **O próprio número mora em Integrações desde a 11.2.** Ele aparece no cabeçalho da página
 * ("seu número: +55 …", protótipo v2) ao lado do link "Meu número", que é o caminho para conectar,
 * desconectar e parear — e a recusa `409` do envio leva ao mesmo lugar.
 *
 * **A recusa do serviço vai à tela verbatim**, em `role="alert"`: `403`, `409` e `503` chegam do
 * envelope único em PT-BR, e reescrevê-las aqui seria uma segunda redação da mesma frase.
 *
 * **Os bytes não passam por este componente.** O `src` de cada mídia aponta direto para
 * `GET /midias/{id}`; a prévia antes do envio usa o próprio arquivo local (`URL.createObjectURL`).
 *
 * **A região das mensagens é uma área viva** (`aria-live="polite"`, `aria-relevant="additions"`):
 * é ela que faz a mensagem do cliente chegar a quem usa leitor de tela, e só o `<li>` novo é lido.
 */

import { useQueries, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import {
  chaveDaEmpresa,
  lerEmpresa,
  useNomesDeEmpresas,
  useNomesDePessoas,
} from '@/modulos/cadastro/api'
import {
  ChipsDaConversa,
  conteinerDoPainel,
  usePosicaoDoPainel,
} from '@/modulos/cadastro/etiquetas'
import { normalizarParaComparar } from '@/modulos/cadastro/nome-da-empresa'
import {
  type NegocioNoQuadro,
  chaveDosNegociosDaEmpresa,
  listarNegociosDaEmpresa,
  PRAZO_DA_ESPERA_DO_N0,
  useNegociosDaPessoa,
} from '@/modulos/negocios/api'
import {
  EMPRESA_A_DEFINIR,
  NEGOCIOS_DO_CONTATO_INDISPONIVEIS,
} from '@/modulos/negocios/apresentacao'
import { useSessao } from '@/modulos/identidade/sessao'
import { useAbrirNegocio } from '@/modulos/negocios/navegacao-do-negocio'
import { AcoesDoCabecalho, useEhCelular, useToqueNaTela } from '@/shell/Shell'
import { CAMINHOS, ehDeGestao } from '@/shell/navegacao'
import {
  type AnexoPendente,
  AnexosParaEnviar,
  SOLTE_AQUI,
  anexoPendente,
  liberarAnexo,
} from './AnexosParaEnviar'
import {
  CHAVE_DO_TOTAL_DE_NAO_LIDAS,
  TETO_DO_CONTADOR,
  type Conversa,
  type Mensagem,
  useEnviarMensagem,
  useEnviarMidia,
  useMarcarLeitura,
  useMensagens,
  useReagir,
  useSituacaoDoMeuNumero,
} from './api'
import { numeroCasa, prepararTermo } from './busca-por-telefone'
import {
  CONVERSA_SAIU_DA_LISTA,
  DADOS_DO_CONTATO,
  DadosDoContato,
  ID_DO_NOME_DO_CONTATO,
  VER_OS_DADOS_DO_CONTATO,
  contatoVinculadoA,
  maisOpcoesDe,
  opcoesDe,
  useModoDoPainel,
} from './DadosDoContato'
import {
  CONTEXTO_SEM_NOME_DA_EMPRESA,
  type RotulosDoCadastro,
  contextoDaConversa,
  empresasDaConversa,
  formatarNumeroDeWhatsapp,
} from './contexto-da-conversa'
import { navegadorGravaAudio, useGravacaoDeAudio } from './gravacao-de-audio'
import { BotaoDeIniciarConversa, DialogoDeIniciarConversa } from './IniciarNovaConversa'
import {
  BAIXAR,
  FORMATOS_ACEITOS,
  IMAGEM_SEM_LEGENDA,
  baixarDescrito,
  formatarDuracao,
  rotuloDaMensagem,
  rotuloDaPrevia,
  rotuloDoOrcamentoEnviado,
  urlDaMidia,
} from './midia'
import './conversas.css'

/** `id` do título da coluna — é ele que nomeia a lista para o leitor de tela. */
const ID_DO_TITULO_DA_LISTA = 'conversas-titulo'

export const TITULO_DA_LISTA = 'Suas conversas'
/**
 * A busca da lista. O protótipo v2 dizia "Pesquisar ou começar conversa"; desde a 13.21b começar é
 * o lápis ao lado ("Iniciar nova conversa"), e a busca só pesquisa.
 */
export const ROTULO_DA_BUSCA_DE_CONVERSAS = 'Pesquisar conversa'
export const NENHUMA_CONVERSA_NA_BUSCA = 'Nenhuma conversa corresponde à busca.'

/** Os dois filtros da lista (protótipo v2: "Todas" e "Não lidas"). */
export const FILTRO_TODAS = 'Todas'
/**
 * **"Não lidas" de verdade desde a 13.8** (FR-87): a leitura é fato do serviço
 * (`leitura_da_conversa`), e não mais "a última fala é do cliente" (o "A responder" da quarta
 * passada, DW-355). O filtro mostra as conversas não lidas que **quem olha lê** — as mesmas que o
 * total do menu soma (D-9).
 */
export const FILTRO_NAO_LIDAS = 'Não lidas'
export const NENHUMA_NAO_LIDA = 'Nenhuma conversa não lida.'

/** Os dois itens de leitura do menu da linha (13.8, FR-87) — só para quem lê a conversa. */
export const MARCAR_COMO_LIDA = 'Marcar como lida'
export const MARCAR_COMO_NAO_LIDA = 'Marcar como não lida'
/** O que o leitor de tela ouve quando a marca muda pelo menu (13.14c, DW-428). */
export const conversaMarcadaComo = (nome: string, lida: boolean): string =>
  `Conversa de ${nome} marcada como ${lida ? 'lida' : 'não lida'}.`
/** O intervalo entre limpar e repor o anúncio: o mesmo texto de novo também é lido. */
const PAUSA_DO_ANUNCIO_EM_MS = 100
/** O que o leitor de tela diz do contador da marcada sem mensagem nova. */
export const MARCADA_COMO_NAO_LIDA = 'Marcada como não lida'
/** Acima disto o contador escreve "99+" (D-10) — o mesmo teto do menu lateral. */
export { TETO_DO_CONTADOR } from './api'

/**
 * A linha de apoio da coluna (protótipo `:387`), mantida como texto acessível do título: ela diz,
 * em uma frase, a regra do envio (FR-71). Até a 13.11c dizia "a conversa segue o cliente, não o
 * número"; desde a visibilidade simples (DW-387 fechada) a conversa é do número — uma por número, um
 * contato só —, e cada pessoa responde pelo próprio.
 */
export const APOIO_DA_LISTA = 'Número próprio da Orvalho · cada pessoa responde pelo próprio número'

/**
 * O que fica no lugar do compositor na conversa de outro número (13.11c, FR-71): o Responsável da
 * Empresa e a gestão leem a conversa, e só a dona do número envia por ela. Informativo — desde a
 * 13.21b começar conversa é o "Iniciar nova conversa" da lista, e ele abre sempre pelo **próprio**
 * número de quem pede.
 */
export const CONVERSA_DE_OUTRO_NUMERO =
  'Esta conversa entrou pelo número de outra pessoa: aqui você acompanha, e só a dona do número ' +
  'envia por ela.'

export const CARREGANDO_CONVERSAS = 'Carregando as suas conversas…'
export const SEM_CONVERSAS =
  'Nenhuma conversa ainda. Elas aparecem aqui quando alguém escreve para o seu número.'
export const SEM_CONVERSA_ABERTA = 'Escolha uma conversa na lista ao lado.'
export const CARREGANDO_MENSAGENS = 'Carregando a conversa…'
export const SEM_MENSAGENS = 'Nenhuma mensagem nesta conversa ainda.'
export const TENTAR_DE_NOVO = 'Tentar de novo'

export const VOLTAR_PARA_A_LISTA = 'Conversas'
/** O campo de escrever, com as palavras do protótipo v2 e do próprio WhatsApp. */
export const RASCUNHO = 'Digite uma mensagem'
export const ENVIAR = 'Enviar'
export const ENVIANDO = 'Enviando…'
/** A marca da mensagem que saiu — um tique, como o "enviada" do WhatsApp. */
export const ENVIADA = 'Enviada'

/** O clipe: abre o menu de tipos de anexo, como o "+" do WhatsApp Web. */
export const ANEXAR = 'Anexar'
export const MENU_DE_ANEXOS = 'O que anexar'
export const ANEXAR_FOTOS_E_VIDEOS = 'Fotos e vídeos'
export const ANEXAR_DOCUMENTO = 'Documento'
export const ANEXAR_AUDIO = 'Áudio'
/** O `accept` de cada item do menu — sugestão ao seletor do sistema; quem decide é o serviço. */
const ACEITA_FOTOS_E_VIDEOS = 'image/*,video/*'
const ACEITA_DOCUMENTOS = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip'
const ACEITA_AUDIO = 'audio/*'

export const GRAVAR_AUDIO = 'Gravar um áudio'
export const PARAR_E_ENVIAR = 'Enviar o áudio'
export const CANCELAR_GRAVACAO = 'Cancelar a gravação'
export const GRAVANDO = 'Gravando'

/**
 * O caminho até o **próprio número**, que desde a 11.2 mora em Integrações → WhatsApp. É o rótulo
 * do link do cabeçalho e da ação que acompanha o `409` de número desconectado.
 */
export const ABRIR_MEU_NUMERO = 'Meu número'
/** O prefixo do próprio número no cabeçalho (protótipo v2: "seu número: (49) 9 9812-3344"). */
export const SEU_NUMERO = 'seu número:'

/** O botão do cabeçalho do chat que abre o modal do Negócio (11.12). */
export const ABRIR_NEGOCIO = 'Abrir negócio'
/** A ação quando não há Negócio em andamento — a Pessoa solta inclusive (protótipo v2). */
export const NOVO_NEGOCIO_PARA_A_PESSOA = '+ Negócio para esta pessoa'

/** O nome acessível da escolha quando a Empresa tem mais de um Negócio em andamento. */
export const ESCOLHA_O_NEGOCIO = 'Escolha o negócio'

/** O sinal do botão enquanto a leitura dos Negócios da Empresa não chega. */
export const CARREGANDO_NEGOCIOS = 'Carregando os negócios desta empresa…'

/** A frase da leitura recusada — o botão fica, desabilitado, e diz por quê. */
export const NEGOCIOS_INDISPONIVEIS = 'Não foi possível carregar os negócios desta empresa agora.'

/**
 * O rótulo de cada Negócio na escolha: o código (o ID que o time fala em voz alta) e os dias sem
 * contato — o bastante para distinguir dois Negócios da mesma Empresa sem abrir nenhum.
 */
export function rotuloDoNegocioParaEscolher(negocio: NegocioNoQuadro): string {
  const dias = negocio.dias_sem_contato
  const unidade = dias === 1 ? 'dia' : 'dias'
  return `Negócio #${String(negocio.codigo)} · ${String(dias)} ${unidade} sem contato`
}

/** A faixa do número desconectado (State Patterns da espinha) — o nome acessível dela. */
export const WHATSAPP_DESCONECTADO = 'WhatsApp desconectado'

/** O texto da faixa: a situação e a consequência, em uma frase. */
export const FRASE_DO_DESCONECTADO =
  'Desconectado — as conversas continuam legíveis, mas nada sai até o seu número voltar.'

/** O link da faixa até onde se conecta o número (11.2). */
export const ABRIR_INTEGRACOES = 'Abrir Integrações'

/** A faixa do número em pareamento — o nome acessível dela. */
export const WHATSAPP_PAREANDO = 'WhatsApp pareando'

/** O texto da faixa de pareamento: ainda não sai nada, e onde terminar. */
export const NUMERO_PAREANDO =
  'Pareando — as conversas continuam legíveis; termine a conexão em Integrações para enviar.'

/** O `id` da faixa da situação do número — é para ela que o compositor aponta a razão. */
const ID_DA_FAIXA_DO_NUMERO = 'conversas-situacao-do-numero'

/** A permissão que o "Iniciar nova conversa" segue (FR-61-A: "Iniciar conversa segue 'Enviar mensagem'"). */
const PERMISSAO_DE_INICIAR_CONVERSA = 'enviar_mensagem'

/**
 * O conjunto fixo de reações — os seis da bandeja padrão do WhatsApp (8.6, FR-76). Cópia declarada
 * do `EMOJIS_DE_REACAO` do serviço: o `openapi.json` não publica o conjunto de um validador de corpo.
 */
export const EMOJIS_DE_REACAO = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const

/** O gesto de reagir — o `aria-label` e o `title` do botão de cada balão. */
export const REAGIR = 'Reagir'

/** O nome acessível da bandeja de emojis que o gesto abre. */
export const ESCOLHA_UMA_REACAO = 'Escolha uma reação'

export const rotuloDaReacaoRecebida = (emoji: string): string => `Reação do contato: ${emoji}`
export const rotuloDaReacaoEnviada = (emoji: string): string => `Sua reação: ${emoji}`
/** A reação que saiu pelo número de outra pessoa (13.11c): quem lê não foi quem reagiu. */
export const rotuloDaReacaoEnviadaPorOutro = (emoji: string): string => `Reação enviada: ${emoji}`

/**
 * Se a conversa é de outro número (13.11c, `do_meu_numero`). **Só o `false` explícito** trava: o
 * servidor anterior ao campo (um rollback da VPS com o SPA novo) manda a conversa sem ele, e aí quem
 * decide é o `403` do envio, como antes.
 */
export function ehDeOutroNumero(conversa: Conversa): boolean {
  return (conversa as Partial<Conversa>).do_meu_numero === false
}
export const rotuloDeReagirCom = (emoji: string): string => `Reagir com ${emoji}`
export const rotuloDeRemoverAReacao = (emoji: string): string => `Remover a reação ${emoji}`

/**
 * A frase do `409` de número desconectado, **como o serviço a escreve** — cópia declarada, no
 * padrão do repositório de comparar a mensagem verbatim. Ela separa os dois `409` da reação.
 */
export const NUMERO_DESCONECTADO_DO_SERVICO =
  'Conecte o seu numero de WhatsApp antes de enviar: sem ele nao ha por onde a mensagem sair.'

/** Os ids do gesto e da bandeja daquela mensagem — o par que `aria-controls` e o foco amarram. */
export const idDoGestoDeReagir = (mensagemId: string): string => `reagir-${mensagemId}`
export const idDaBandejaDeReacoes = (mensagemId: string): string => `reacoes-${mensagemId}`

/** O rótulo da região viva das mensagens — é ele que o leitor de tela anuncia ao entrar nela. */
export const MENSAGENS_DA_CONVERSA = 'Mensagens da conversa'

/** O aviso do link que aponta para uma conversa que esta tela não carregou. */
export const CONVERSA_DO_LINK_NAO_ESTA_NA_LISTA =
  'A conversa deste link não está na sua lista — ela pode ser mais antiga do que as que esta tela ' +
  'carrega, ou não estar disponível para você. Abrimos a conversa mais recente.'

/** A prévia de uma conversa aberta por um aviso sem corpo — um estado legítimo desde a 8.2. */
export const SEM_PREVIA = 'Sem mensagens ainda'

/** O teto do campo de escrever — o mesmo da coluna `mensagem.texto`, que é o do próprio WhatsApp. */
export const TAMANHO_MAXIMO_DO_TEXTO = 65_536

/** O `409` do envelope único: o próprio número não está conectado, e há remédio na tela. */
const STATUS_DE_CONFLITO = 409

/** O `404` da ficha da Empresa: quem lê a conversa não vê a Empresa dela (13.11c, D-4). */
const STATUS_NAO_ENCONTRADO = 404

/** O aviso de que parte dos nomes não chegou. */
export const NOMES_INDISPONIVEIS =
  'Alguns nomes não puderam ser carregados agora; as conversas aparecem pelo número.'

/** A hora do balão: só hora e minuto, no fuso de quem trabalha (NFR-8). */
const HORA = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Sao_Paulo',
})

/** O dia da semana abreviado, para a conversa da semana passada. */
const DIA_DA_SEMANA = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'short',
  timeZone: 'America/Sao_Paulo',
})

/** O dia da semana por extenso, para o separador de dia. */
const DIA_DA_SEMANA_LONGO = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'long',
  timeZone: 'America/Sao_Paulo',
})

/** Dia e mês, para o que é mais velho que uma semana. */
const DIA_E_MES = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  timeZone: 'America/Sao_Paulo',
})

/** A data completa, para o separador de dia além da semana. */
const DATA_COMPLETA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'America/Sao_Paulo',
})

/** O dia civil daquele instante em São Paulo — é por ele que os dias se comparam. */
const DIA_CIVIL = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'America/Sao_Paulo',
})

const UM_DIA = 24 * 60 * 60 * 1000

/**
 * Quantos **dias civis** separam dois instantes, no fuso de quem trabalha. As duas datas viram
 * meia-noite UTC só para a subtração — nenhuma delas representa um instante, e é por isso que o fuso
 * já foi aplicado antes, ao formatar.
 */
function diasCivisEntre(instante: Date, agora: Date): number {
  const paraUtc = (data: Date) => Date.parse(`${DIA_CIVIL.format(data)}T00:00:00Z`)
  return Math.round((paraUtc(agora) - paraUtc(instante)) / UM_DIA)
}

/** Quantos dias civis o carimbo ainda mostra o dia da semana. No sétimo, ele vira data. */
const DIAS_COM_NOME_DA_SEMANA = 6

/**
 * O carimbo curto da lista (protótipo: `09:12`, `ontem`, `seg`). Os três degraus medem a mesma
 * unidade — dia civil — no fuso de quem trabalha (NFR-8).
 */
export function horaDaLista(iso: string, agora: Date = new Date()): string {
  const instante = new Date(iso)
  if (Number.isNaN(instante.getTime())) {
    // Contrato que mudou de forma: melhor o texto cru do que um `Invalid Date` na tela.
    return iso
  }
  const dias = diasCivisEntre(instante, agora)
  if (dias <= 0) {
    // Hoje — e o futuro (relógios fora de sincronia) cai aqui, que é o degrau mais informativo.
    return HORA.format(instante)
  }
  if (dias === 1) {
    return 'ontem'
  }
  if (dias <= DIAS_COM_NOME_DA_SEMANA) {
    // O `.` que o `pt-BR` põe em "seg." não cabe na coluna, e o protótipo escreve sem ele.
    return DIA_DA_SEMANA.format(instante).replace('.', '')
  }
  return DIA_E_MES.format(instante)
}

/** A hora de um balão. Um instante inválido volta verbatim, pela mesma razão da função acima. */
export function horaDoBalao(iso: string): string {
  const instante = new Date(iso)
  return Number.isNaN(instante.getTime()) ? iso : HORA.format(instante)
}

/**
 * O separador de dia do chat (protótipo v2 `.dia`: "Hoje"), com os degraus do WhatsApp: hoje,
 * ontem, o dia da semana dentro da semana, e a data completa além dela.
 */
export function rotuloDoDia(iso: string, agora: Date = new Date()): string {
  const instante = new Date(iso)
  if (Number.isNaN(instante.getTime())) {
    return iso
  }
  const dias = diasCivisEntre(instante, agora)
  if (dias <= 0) {
    return 'Hoje'
  }
  if (dias === 1) {
    return 'Ontem'
  }
  if (dias <= DIAS_COM_NOME_DA_SEMANA) {
    const nome = DIA_DA_SEMANA_LONGO.format(instante)
    return nome.charAt(0).toLocaleUpperCase('pt-BR') + nome.slice(1)
  }
  return DATA_COMPLETA.format(instante)
}

/** O dia civil de um instante — a chave pela qual as mensagens se agrupam sob um separador. */
function diaCivilDe(iso: string): string {
  const instante = new Date(iso)
  return Number.isNaN(instante.getTime()) ? iso : DIA_CIVIL.format(instante)
}

/**
 * A prévia da última mensagem, com o `Você:` do protótipo quando foi o Vendedor quem falou — e só na
 * conversa do próprio número (13.11c): na de outro, quem falou foi a dona dele, e não quem lê.
 * Conversa aberta sem mensagem nenhuma é um estado legítimo, e ali a prévia é uma frase que diz isso.
 */
export function previaDaConversa(conversa: Conversa): string {
  const daMidia = rotuloDaPrevia(conversa)
  const texto = daMidia ?? conversa.previa
  if (texto === null) {
    return SEM_PREVIA
  }
  return conversa.previa_direcao === 'enviada' && !ehDeOutroNumero(conversa)
    ? `Você: ${texto}`
    : texto
}

/**
 * A leitura de uma conversa, tolerando o serviço anterior à 13.8 (a janela do deploy): sem os três
 * campos, a conversa sai lida e sem "Marcar como…".
 */
export function leituraDa(conversa: Conversa): {
  naoLidas: number
  marcada: boolean
  podeMarcar: boolean
} {
  const campos = conversa as Partial<Conversa>
  return {
    naoLidas: campos.nao_lidas ?? 0,
    marcada: campos.marcada_nao_lida === true,
    podeMarcar: campos.pode_marcar_leitura === true,
  }
}

/** O conjunto sem aquele id — o mesmo objeto quando ele não estava lá (não re-renderiza). */
function semA(conjunto: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (!conjunto.has(id)) {
    return conjunto
  }
  const sem = new Set(conjunto)
  sem.delete(id)
  return sem
}

/** A conversa não lida: recebidas depois da marca, ou "marcada como não lida" (FR-87). */
export function estaNaoLida(conversa: Conversa): boolean {
  const { naoLidas, marcada } = leituraDa(conversa)
  return naoLidas > 0 || marcada
}

/** O que o leitor de tela diz do contador da linha. */
export function rotuloDoContador(naoLidas: number): string {
  if (naoLidas <= 0) {
    return MARCADA_COMO_NAO_LIDA
  }
  return naoLidas === 1 ? '1 mensagem não lida' : `${String(naoLidas)} mensagens não lidas`
}

/** O número desenhado no contador: vazio na marcada sem nova, "99+" acima do teto. */
export function textoDoContador(naoLidas: number): string {
  if (naoLidas <= 0) {
    return ''
  }
  return naoLidas > TETO_DO_CONTADOR ? `${String(TETO_DO_CONTADOR)}+` : String(naoLidas)
}

/** A mensagem de uma recusa do serviço, como ela veio — ou a genérica, quando não é `ErroDaApi`. */
function mensagemDoErro(erro: unknown): string {
  return erro instanceof ErroDaApi ? erro.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** Um aviso do serviço, com **uma** ação opcional ao lado. */
function Alerta({
  texto,
  rotuloDaAcao,
  aoAgir,
}: {
  texto: string
  rotuloDaAcao?: string
  aoAgir?: () => void
}) {
  return (
    <p className="conversas__erro" role="alert">
      {texto}
      {aoAgir === undefined ? null : (
        <button type="button" className="conversas__tentar" onClick={aoAgir}>
          {rotuloDaAcao ?? TENTAR_DE_NOVO}
        </button>
      )}
    </p>
  )
}

/**
 * Quantas etiquetas cabem na linha da lista antes do "+N" (D-9 da 13.6); o cabeçalho mostra todas.
 * Desde a 13.18 o teto é dividido: primeiro as da Pessoa, depois as da Empresa, e o "+N" conta as
 * duas (`ChipsDaConversa`).
 */
const ETIQUETAS_NA_LINHA = 2

/**
 * A mídia dentro de um balão — a metade visual do FR-41. Quatro desenhos: a imagem aparece na tela
 * e abre em aba própria; o vídeo tem controles; o áudio toca dentro da tela, com duração; o
 * documento tem nome, tamanho e "Baixar". Todos ganham "Baixar" (`?anexo=1`).
 */
function MidiaDoBalao({ mensagem }: { mensagem: Mensagem }) {
  //: 13.22c1: a mensagem que levou a foto do Orçamento do CRM diz o envio; o balão do documento vem
  //: junto para quem recebe o arquivo, e some para a SDR (o servidor tira a mídia dela, T7).
  const numeroDoOrcamento = mensagem.numero_do_orcamento_do_crm ?? null
  if (numeroDoOrcamento !== null) {
    return (
      <span className="conversas__orcamento-enviado">
        <span className="conversas__orcamento-rotulo">
          {rotuloDoOrcamentoEnviado(numeroDoOrcamento)}
        </span>
        {mensagem.tipo_de_midia === null ? null : (
          <MidiaDoBalao mensagem={{ ...mensagem, numero_do_orcamento_do_crm: null }} />
        )}
      </span>
    )
  }
  if (mensagem.tipo_de_midia === null) {
    return null
  }
  const fonte = urlDaMidia(mensagem.id)
  const paraBaixar = urlDaMidia(mensagem.id, { anexo: true })
  const rotulo = rotuloDaMensagem(mensagem)

  const baixar = (
    <a
      className="conversas__baixar"
      href={paraBaixar}
      download={mensagem.nome_do_arquivo ?? undefined}
      aria-label={baixarDescrito(rotulo)}
    >
      {BAIXAR}
    </a>
  )

  if (mensagem.tipo_de_midia === 'imagem') {
    return (
      <span className="conversas__midia">
        <a href={fonte} target="_blank" rel="noreferrer" aria-label={rotulo ?? IMAGEM_SEM_LEGENDA}>
          <img
            className="conversas__imagem"
            src={fonte}
            alt={mensagem.texto === null ? IMAGEM_SEM_LEGENDA : ''}
            loading="lazy"
          />
        </a>
        <span className="conversas__midia-rodape">
          <span className="conversas__midia-rotulo">{rotulo}</span>
          {baixar}
        </span>
      </span>
    )
  }

  if (mensagem.tipo_de_midia === 'video') {
    return (
      <span className="conversas__midia">
        <video
          className="conversas__video"
          src={fonte}
          controls
          preload="metadata"
          aria-label={rotulo ?? undefined}
        />
        <span className="conversas__midia-rodape">
          <span className="conversas__midia-rotulo">{rotulo}</span>
          {baixar}
        </span>
      </span>
    )
  }

  if (mensagem.tipo_de_midia === 'audio') {
    return (
      <span className="conversas__midia">
        <audio
          className="conversas__audio"
          src={fonte}
          controls
          preload="metadata"
          aria-label={rotulo ?? undefined}
        />
        <span className="conversas__midia-rodape">
          <span className="conversas__midia-rotulo">{rotulo}</span>
          {baixar}
        </span>
      </span>
    )
  }

  return (
    <span className="conversas__midia">
      <span className="conversas__documento">
        <span className="conversas__documento-icone" aria-hidden="true">
          📄
        </span>
        <span className="conversas__midia-rotulo">{rotulo}</span>
        {baixar}
      </span>
    </span>
  )
}

/**
 * Os chips de reação, ancorados no canto do balão (8.6, FR-76; protótipo `.balao__reacoes`). Uma
 * por lado, como no próprio WhatsApp, distintas pelo `data-lado` e pelo nome acessível.
 */
function ReacoesDoBalao({ mensagem, deOutro }: { mensagem: Mensagem; deOutro: boolean }) {
  const recebida = mensagem.reacao_recebida ?? null
  const enviada = mensagem.reacao_enviada ?? null
  if (recebida === null && enviada === null) {
    return null
  }
  return (
    <span className="conversas__reacoes">
      {recebida === null ? null : (
        <span
          className="conversas__reacao"
          data-lado="recebida"
          role="img"
          aria-label={rotuloDaReacaoRecebida(recebida)}
        >
          {recebida}
        </span>
      )}
      {enviada === null ? null : (
        <span
          className="conversas__reacao"
          data-lado="enviada"
          role="img"
          aria-label={
            deOutro ? rotuloDaReacaoEnviadaPorOutro(enviada) : rotuloDaReacaoEnviada(enviada)
          }
        >
          {enviada}
        </span>
      )}
    </span>
  )
}

/**
 * O "+ Negócio para esta pessoa": abre o Funil já com a janela de Novo negócio (`?novo=1`) — e,
 * desde a 13.1, **levando a conversa** (fecha a DW-360). Com Empresa, `empresa=<id>`: a janela abre
 * com ela escolhida. Pessoa solta, `pessoa=<id>`: a janela relê a Pessoa, a ficha nova traz o nome e
 * o telefone dela, e o Negócio criado a vincula. Sem Pessoa, só `?novo=1`, como antes.
 *
 * **Na URL vão só ids.** O nome exibido do contato vai no *state* do histórico, e só quando ele tem
 * letra: sem letra ele é o próprio número formatado, e telefone não viaja nem ali. Nome e telefone
 * de gente não ficam em barra de endereço nem em log de proxy. O *state* fica na entrada do
 * histórico da sessão (a do navegador, desta aba) até a tela do Funil a substituir pelo `replace`
 * que limpa a URL.
 */
function NovoNegocioParaAPessoa({
  empresaId,
  pessoaId,
  nome,
}: {
  empresaId: string | null
  pessoaId: string | null
  nome?: string
}) {
  const daConversa =
    empresaId !== null ? { empresa: empresaId } : pessoaId !== null ? { pessoa: pessoaId } : {}
  return (
    <Link
      className="conversas__abrir-negocio"
      to={CAMINHOS.funil}
      search={{ novo: true, ...daConversa }}
      state={nome === undefined || !/\p{L}/u.test(nome) ? undefined : { nomeDoContato: nome }}
    >
      {NOVO_NEGOCIO_PARA_A_PESSOA}
    </Link>
  )
}

/** O "+ Negócio" de uma das Empresas da Pessoa, na escolha do cabeçalho (13.19c2). */
export function novoNegocioEm(nomeDaEmpresa: string): string {
  return `+ Negócio em ${nomeDaEmpresa}`
}

/** O nome acessível da escolha da Empresa do Negócio novo (13.19c2). */
export const ESCOLHA_A_EMPRESA_DO_NEGOCIO = 'Escolha a empresa do negócio'

/** A Empresa cujos Negócios não vieram, ao lado dos que vieram (code review da 13.19c2, E4). */
export function negociosIndisponiveisDe(nomeDaEmpresa: string): string {
  return `Não foi possível carregar os negócios de ${nomeDaEmpresa} agora.`
}

/** O rótulo do Negócio na escolha com mais de uma Empresa: a Empresa dele na frente (E4). */
export function rotuloDoNegocioDaEmpresa(nomeDaEmpresa: string, negocio: NegocioNoQuadro): string {
  return `${nomeDaEmpresa} · ${rotuloDoNegocioParaEscolher(negocio)}`
}

/**
 * O menu do cabeçalho do chat — a escolha do Negócio (11.12) e, desde a 13.19c2, a da Empresa do
 * Negócio novo. **Em portal** (`conteinerDoPainel`, `position: fixed`, camada
 * `--camada-painel-flutuante`), posicionado por `usePosicaoDoPainel` a partir do botão: o topo do
 * chat não o corta. O foco vai à primeira opção **uma vez por abertura**, depois de posicionado (e
 * não a cada mudança de posição). Fecham: o Escape de qualquer lugar (sem `stopPropagation` — quem
 * já tratou o Escape marca `defaultPrevented`), o clique fora, o foco que sai do menu e do botão
 * (o Tab) e o botão sair da janela. Só o Escape devolve o foco ao botão.
 */
function MenuDoCabecalho({
  aberto,
  ancora,
  rotulo,
  aoFechar,
  children,
}: {
  aberto: boolean
  ancora: RefObject<HTMLButtonElement | null>
  rotulo: string
  aoFechar: (devolverFoco: boolean) => void
  children: ReactNode
}) {
  const menu = useRef<HTMLSpanElement | null>(null)
  const fechar = useRef(aoFechar)
  useEffect(() => {
    fechar.current = aoFechar
  })
  const posicao = usePosicaoDoPainel(aberto, ancora, menu, {
    aoSairDaJanela: () => {
      fechar.current(false)
    },
  })

  const focouAoAbrir = useRef(false)
  useEffect(() => {
    if (!aberto) {
      focouAoAbrir.current = false
      return
    }
    if (posicao === null || focouAoAbrir.current) {
      return
    }
    focouAoAbrir.current = true
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
  }, [aberto, posicao])

  useEffect(() => {
    if (!aberto) {
      return
    }
    const dentro = (alvo: EventTarget | null) =>
      alvo instanceof Node &&
      (menu.current?.contains(alvo) === true || ancora.current?.contains(alvo) === true)
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape' && !evento.defaultPrevented) {
        evento.preventDefault()
        fechar.current(true)
      }
    }
    const aoTocarFora = (evento: MouseEvent) => {
      if (!dentro(evento.target)) {
        fechar.current(false)
      }
    }
    /* O foco que sai do menu **e** do botão (o Tab, o clique num controle fora) fecha, sem devolver
       o foco: quem saiu foi para outro lugar de propósito. */
    const aoSairOFoco = (evento: FocusEvent) => {
      if (dentro(evento.target) && !dentro(evento.relatedTarget)) {
        fechar.current(false)
      }
    }
    document.addEventListener('keydown', aoTeclar)
    document.addEventListener('mousedown', aoTocarFora)
    document.addEventListener('focusout', aoSairOFoco)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
      document.removeEventListener('mousedown', aoTocarFora)
      document.removeEventListener('focusout', aoSairOFoco)
    }
  }, [aberto, ancora])

  if (!aberto) {
    return null
  }
  return createPortal(
    <span
      ref={menu}
      className="conversas__escolha-de-negocio"
      role="menu"
      aria-label={rotulo}
      style={posicao ?? { visibility: 'hidden' }}
    >
      {children}
    </span>,
    conteinerDoPainel(ancora.current),
  )
}

/**
 * O "+ Negócio" com mais de uma Empresa em que quem olha cria Negócio e nenhum Negócio em
 * andamento (13.19c2, recomendação do coordenador): o botão de sempre abre a escolha, com um
 * "+ Negócio em <Empresa>" por Empresa — a janela de Novo negócio abre com ela escolhida. O nome é
 * o exibido que o `cadastro` entregou; sem ele, "Empresa vinculada".
 */
function NovoNegocioPorEmpresa({
  empresaIds,
  nomes,
}: {
  empresaIds: readonly string[]
  nomes: ReadonlyMap<string, string>
}) {
  const [escolhendo, setEscolhendo] = useState(false)
  const botao = useRef<HTMLButtonElement | null>(null)
  return (
    <span className="conversas__abrir-negocio-ancora">
      <button
        ref={botao}
        type="button"
        className="conversas__abrir-negocio"
        aria-haspopup="menu"
        aria-expanded={escolhendo}
        onClick={() => {
          setEscolhendo((aberta) => !aberta)
        }}
      >
        {NOVO_NEGOCIO_PARA_A_PESSOA}
      </button>
      <MenuDoCabecalho
        aberto={escolhendo}
        ancora={botao}
        rotulo={ESCOLHA_A_EMPRESA_DO_NEGOCIO}
        aoFechar={(devolverFoco) => {
          setEscolhendo(false)
          if (devolverFoco) {
            botao.current?.focus()
          }
        }}
      >
        {empresaIds.map((empresaId) => (
          <Link
            key={empresaId}
            role="menuitem"
            className="conversas__opcao-de-negocio"
            to={CAMINHOS.funil}
            search={{ novo: true, empresa: empresaId }}
          >
            {novoNegocioEm(nomes.get(empresaId) ?? CONTEXTO_SEM_NOME_DA_EMPRESA)}
          </Link>
        ))}
      </MenuDoCabecalho>
    </span>
  )
}

/**
 * O "Abrir negócio" do cabeçalho do chat (11.12): um Negócio abre o modal da 11.4 direto; vários
 * abrem a escolha; **nenhum** — as Empresas sem Negócio em andamento que quem olha enxergue — e a
 * ação vira "+ Negócio para esta pessoa" (protótipo v2).
 *
 * **Desde a 13.19c2 sobre as Empresas da conversa** (`empresasDaConversa`): os Negócios em andamento
 * de todas elas, numa lista só — com mais de uma Empresa, cada Negócio leva o nome da dele. Sem
 * nenhum Negócio, o "+ Negócio" só das Empresas em que quem olha **cria** Negócio (`criaveis`, as
 * editáveis do `cadastro`; code review, C): com uma, o "+ Negócio para esta pessoa" dela; com mais
 * de uma, um "+ Negócio em <Empresa>" por Empresa. Sem a Pessoa no lote do `cadastro`, `criaveis`
 * é `undefined` ("não sei") e vale a regra de antes: a Empresa cuja ficha dá `404` sai.
 *
 * **Degrada por Empresa** (code review, E4): a leitura no ar deixa o botão desabilitado e ocupado;
 * a que falhou é dita pelo nome, ao lado dos Negócios que vieram — e, sem nenhum Negócio que veio,
 * o botão fica desabilitado (não dá para dizer "nenhum"). A escolha é um menu completo
 * (`MenuDoCabecalho`).
 */
function AbrirNegocio({
  empresaIds,
  criaveis,
  nomes,
  doContato = [],
  carregandoDoContato = false,
  falhouDoContato = false,
}: {
  empresaIds: readonly string[]
  criaveis: readonly string[] | undefined
  nomes: ReadonlyMap<string, string>
  /**
   * 13.20b2 (Y10; code review A1): os Negócios em andamento do contato (`?pessoa_id=`) — o sem
   * Empresa e o ex-N0 —, que o botão conta junto dos das Empresas, sem repetir o que já veio por
   * elas: um só abre o modal direto, vários abrem a escolha.
   */
  doContato?: readonly NegocioNoQuadro[]
  carregandoDoContato?: boolean
  /** O recorte do contato falhou (A2): "não sei" — nenhum "+ Negócio", e o aviso. */
  falhouDoContato?: boolean
}) {
  /*
   * A Empresa que quem lê não vê (13.11c, D-4): a dona do número que não é Responsável nem a vê
   * pela PA-1, pela PA-3 ou pelo Grupo recebe `404` da ficha — e "+ Negócio" a levaria a um Novo
   * negócio com uma Empresa que ela não pode escolher. Só o `404` tira a Empresa: carregando, o botão
   * de hoje; outro erro, o estado de hoje. As mesmas chaves do painel "Dados do contato".
   */
  const fichas = useQueries({
    queries: empresaIds.map((empresaId) => ({
      queryKey: chaveDaEmpresa(empresaId),
      queryFn: () => lerEmpresa(empresaId),
      retry: false,
    })),
  })
  const alcancaveis = empresaIds.filter((_, indice) => {
    const falha = fichas[indice]?.error
    return !(falha instanceof ErroDaApi && falha.status === STATUS_NAO_ENCONTRADO)
  })
  const consultas = useQueries({
    queries: alcancaveis.map((empresaId) => ({
      queryKey: chaveDosNegociosDaEmpresa({ empresaId }),
      queryFn: () => listarNegociosDaEmpresa(empresaId),
      retry: false,
    })),
  })
  const abrir = useAbrirNegocio()
  const [escolhendo, setEscolhendo] = useState(false)
  const botao = useRef<HTMLButtonElement | null>(null)
  /* O Negócio sem Empresa (13.20b2) entra na escolha com "Empresa a definir" no lugar do nome. */
  const nomeDe = (empresaId: string | null) =>
    empresaId === null
      ? EMPRESA_A_DEFINIR
      : (nomes.get(empresaId) ?? CONTEXTO_SEM_NOME_DA_EMPRESA)
  const dasEmpresas = alcancaveis.flatMap((empresaId, indice) =>
    (consultas[indice]?.data ?? []).map((negocio) => ({ empresaId, negocio })),
  )
  const soDoContato = doContato.filter(
    (negocio) => !dasEmpresas.some(({ negocio: outro }) => outro.id === negocio.id),
  )
  const varias =
    new Set([
      ...alcancaveis,
      ...soDoContato.map((negocio) => negocio.empresa_id ?? EMPRESA_A_DEFINIR),
    ]).size > 1
  const negocios: { empresaId: string | null; negocio: NegocioNoQuadro }[] = [
    ...soDoContato.map((negocio) => ({ empresaId: negocio.empresa_id, negocio })),
    ...dasEmpresas,
  ]
  const falharam = alcancaveis.filter((_, indice) => consultas[indice]?.isError === true)
  const [unico] = negocios
  const varios = negocios.length > 1
  const paraCriar =
    criaveis === undefined ? alcancaveis : alcancaveis.filter((id) => criaveis.includes(id))

  if (
    alcancaveis.length === 0 &&
    doContato.length === 0 &&
    !carregandoDoContato &&
    !falhouDoContato
  ) {
    return null
  }
  if (carregandoDoContato || consultas.some((consulta) => consulta.isLoading)) {
    return (
      <button
        type="button"
        className="conversas__abrir-negocio"
        disabled
        aria-busy="true"
        title={CARREGANDO_NEGOCIOS}
      >
        {ABRIR_NEGOCIO}
      </button>
    )
  }
  const avisoDasQueFalharam =
    falharam.length === 0 && !falhouDoContato ? null : (
      <span className="conversas__negocios-indisponiveis" role="status">
        {[
          falhouDoContato ? NEGOCIOS_DO_CONTATO_INDISPONIVEIS : null,
          falharam.length === 0
            ? null
            : varias && falharam.length < alcancaveis.length
              ? falharam.map((empresaId) => negociosIndisponiveisDe(nomeDe(empresaId))).join(' ')
              : NEGOCIOS_INDISPONIVEIS,
        ]
          .filter(Boolean)
          .join(' ')}
      </span>
    )
  if (unico === undefined && (falharam.length > 0 || falhouDoContato)) {
    return (
      <span className="conversas__abrir-negocio-ancora">
        <button type="button" className="conversas__abrir-negocio" disabled>
          {ABRIR_NEGOCIO}
        </button>
        {avisoDasQueFalharam}
      </span>
    )
  }
  if (unico === undefined) {
    const [so] = paraCriar
    if (so === undefined) {
      return null
    }
    return paraCriar.length === 1 ? (
      <NovoNegocioParaAPessoa empresaId={so} pessoaId={null} />
    ) : (
      <NovoNegocioPorEmpresa empresaIds={paraCriar} nomes={nomes} />
    )
  }

  return (
    <span className="conversas__abrir-negocio-ancora">
      <button
        ref={botao}
        type="button"
        className="conversas__abrir-negocio"
        aria-haspopup={varios ? 'menu' : undefined}
        aria-expanded={varios ? escolhendo : undefined}
        onClick={() => {
          if (!varios) {
            abrir(unico.negocio.id)
            return
          }
          setEscolhendo((aberta) => !aberta)
        }}
      >
        {ABRIR_NEGOCIO}
      </button>
      {avisoDasQueFalharam}
      <MenuDoCabecalho
        aberto={escolhendo && varios}
        ancora={botao}
        rotulo={ESCOLHA_O_NEGOCIO}
        aoFechar={(devolverFoco) => {
          setEscolhendo(false)
          if (devolverFoco) {
            botao.current?.focus()
          }
        }}
      >
        {negocios.map(({ empresaId, negocio }) => (
          <button
            key={negocio.id}
            type="button"
            role="menuitem"
            className="conversas__opcao-de-negocio"
            onClick={() => {
              setEscolhendo(false)
              botao.current?.focus()
              abrir(negocio.id)
            }}
          >
            {varias
              ? rotuloDoNegocioDaEmpresa(nomeDe(empresaId), negocio)
              : rotuloDoNegocioParaEscolher(negocio)}
          </button>
        ))}
      </MenuDoCabecalho>
    </span>
  )
}

/**
 * Um mapa do lote que não pisca (code review da 13.19c2, E8): enquanto o lote novo viaja, o que o
 * anterior já sabia continua (sobreposto pelo que já chegou); assentado, vale o novo. A identidade
 * só muda quando o conteúdo muda.
 */
function useMapaEstavel<V>(
  mapa: ReadonlyMap<string, V>,
  carregando: boolean,
): ReadonlyMap<string, V> {
  /* O "valor da renderização anterior" do React: guardado em estado e atualizado durante a
     renderização só quando o conteúdo muda. */
  const [anterior, setAnterior] = useState<ReadonlyMap<string, V>>(mapa)
  const proximo = carregando ? new Map([...anterior, ...mapa]) : mapa
  const igual =
    proximo.size === anterior.size &&
    [...proximo].every(([chave, valor]) => anterior.has(chave) && anterior.get(chave) === valor)
  if (!igual) {
    setAnterior(proximo)
  }
  return igual ? anterior : proximo
}

interface PropsDaLista {
  conversas: readonly Conversa[]
  rotulos: RotulosDoCadastro
  /** Se os rótulos do `cadastro` ainda estão no ar — `aria-busy` na lista enquanto isso. */
  rotulosACaminho: boolean
  abertaId: string | undefined
  carregando: boolean
  erro: unknown
  ehCelular: boolean
  aoEscolher: (conversaId: string) => void
  /** "Dados do contato" do menu da linha (13.3): abre a conversa **e** o painel. */
  aoAbrirDados: (conversaId: string) => void
  aoRecarregar: () => void
  /** O filtro "Não lidas" e a busca moram em `Conversas` (13.8): no celular a lista desmonta. */
  soNaoLidas: boolean
  aoMudarFiltro: (soNaoLidas: boolean) => void
  busca: string
  aoMudarBusca: (busca: string) => void
  /** As conversas lidas nesta vista do filtro: elas ficam até o filtro mudar (D-19). */
  mantidasNoFiltro: ReadonlySet<string>
  /** "Marcar como lida"/"Marcar como não lida" do menu da linha (13.8). */
  aoMarcar: (conversaId: string, lida: boolean) => void
  /**
   * O lápis "Iniciar nova conversa" (13.21b), ao lado da busca — `null` para quem não tem
   * `enviar_mensagem`. Ele mora em `Conversas`, como a busca: o diálogo sobrevive à lista que
   * desmonta no celular.
   */
  inicioDeConversa: ReactNode
}

/**
 * O termo sem os caracteres de formato (`\p{Cf}`: o espaço de largura zero, as marcas de direção)
 * que vêm colados de mensagens do WhatsApp — o `trim` não os tira, e o termo não acharia nada.
 */
function semInvisiveis(texto: string): string {
  return texto.replace(/\p{Cf}/gu, '')
}

/** O `id` do `<li>` de uma conversa — é nele que o menu da linha se ancora. */
const idDaLinha = (conversaId: string): string => `conversa-linha-${conversaId}`

function ListaDeConversas({
  conversas,
  rotulos,
  rotulosACaminho,
  abertaId,
  carregando,
  erro,
  ehCelular,
  aoEscolher,
  aoAbrirDados,
  aoRecarregar,
  soNaoLidas,
  aoMudarFiltro,
  busca,
  aoMudarBusca,
  mantidasNoFiltro,
  aoMarcar,
  inicioDeConversa,
}: PropsDaLista) {
  const houveRecusa = erro !== null && erro !== undefined
  /* O "⋯" do menu da linha: no celular e, desde a 13.14c (DW-426), em toda tela de toque. */
  const toqueNaTela = useToqueNaTela()
  const comMaisOpcoes = ehCelular || toqueNaTela
  /* A busca da lista (protótipo v2): recorte de leitura sobre as conversas já lidas — pelo nome
     do contexto, pela razão social da linha, pelo número ou pela prévia. Desde o chamado de 21/10
     o número casa também por dígitos quando o termo tem cara de telefone (`busca-por-telefone.ts`):
     `(22) 99755-0997` acha `5522997550997`. O termo é preparado **uma** vez, fora do `filter`. */
  const termo = semInvisiveis(busca).trim().toLowerCase()
  const telefone = prepararTermo(termo)
  /* 13.14c (DW-362): a mesma normalização da busca de Empresas (`normalizar_para_busca` no
     `cadastro`, `normalizarParaComparar` aqui), **somada** à regra de antes — nada que achava deixa
     de achar. Ela fica de fora quando o termo não tem letra nem dígito ("&" viraria "e" e acharia
     quase tudo) e quando o termo é de telefone (os dígitos juntos casariam textos com números). */
  const termoNormalizado =
    telefone === null && /[\p{L}\p{N}]/u.test(termo) ? normalizarParaComparar(termo) : ''
  const casaNormalizado = (texto: string | null | undefined): boolean =>
    termoNormalizado !== '' &&
    texto !== null &&
    texto !== undefined &&
    normalizarParaComparar(texto).includes(termoNormalizado)
  const filtradas = conversas.filter((conversa) => {
    /* "Não lidas" (13.8): as não lidas que quem olha lê, mais as lidas nesta vista (D-19). */
    if (
      soNaoLidas &&
      !(leituraDa(conversa).podeMarcar && estaNaoLida(conversa)) &&
      !mantidasNoFiltro.has(conversa.id)
    ) {
      return false
    }
    if (termo === '') {
      return true
    }
    const contexto = contextoDaConversa(conversa, rotulos)
    return (
      contexto.nome.toLowerCase().includes(termo) ||
      (contexto.empresa?.toLowerCase().includes(termo) ?? false) ||
      //: 13.7: a busca acha a Empresa também pela razão social, que a linha não mostra.
      (contexto.razaoSocialDaEmpresa?.toLowerCase().includes(termo) ?? false) ||
      conversa.numero.includes(termo) ||
      numeroCasa(conversa.numero, telefone) ||
      previaDaConversa(conversa).toLowerCase().includes(termo) ||
      casaNormalizado(contexto.nome) ||
      casaNormalizado(contexto.empresa) ||
      casaNormalizado(contexto.razaoSocialDaEmpresa) ||
      //: Só o texto da mensagem: o "Você:" e o rótulo de mídia da prévia decorada não contam.
      casaNormalizado(conversa.previa)
    )
  })
  const listaPronta = !carregando && !houveRecusa && conversas.length > 0

  /*
   * O menu da linha (13.3): o clique direito — e o Shift+F10, a tecla Menu e o toque longo do
   * Android, que o navegador entrega como o mesmo `contextmenu` — ou o "⋯" do celular. Ele fica
   * **preso ao `conversa.id`** e ancorado na própria linha, e não nas coordenadas do evento: o
   * teclado não tem coordenada, e a linha é o que a pessoa apontou. Escape, o clique fora ou a
   * linha sumir da lista (a busca, o filtro, a releitura) fecham.
   */
  const [menuDe, setMenuDe] = useState<string | null>(null)
  const quemAbriuOMenu = useRef<HTMLElement | null>(null)
  /* A linha que sumiu leva o menu junto — ajustado no render, e não num efeito: o menu não pode
     reaparecer sozinho quando a linha voltar. */
  if (menuDe !== null && !filtradas.some((conversa) => conversa.id === menuDe)) {
    setMenuDe(null)
  }
  useEffect(() => {
    if (menuDe === null) {
      return
    }
    document
      .getElementById(idDaLinha(menuDe))
      ?.querySelector<HTMLButtonElement>('[role="menuitem"]')
      ?.focus()
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.preventDefault()
        evento.stopPropagation()
        setMenuDe(null)
        quemAbriuOMenu.current?.focus()
      }
    }
    /* `pointerdown`, e não `mousedown`: o toque também fecha (no celular o "⋯" é tocado). */
    const aoTocarFora = (evento: PointerEvent) => {
      const linha = document.getElementById(idDaLinha(menuDe))
      if (linha?.contains(evento.target as Node) !== true) {
        setMenuDe(null)
      }
    }
    document.addEventListener('keydown', aoTeclar)
    document.addEventListener('pointerdown', aoTocarFora)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
      document.removeEventListener('pointerdown', aoTocarFora)
    }
  }, [menuDe])

  return (
    <div className="conversas__lista" data-celular={ehCelular ? 'sim' : 'nao'}>
      <h2 className="conversas__lista-titulo" id={ID_DO_TITULO_DA_LISTA} title={APOIO_DA_LISTA}>
        {TITULO_DA_LISTA}
      </h2>
      <div className="conversas__lista-topo">
        <label className="conversas__busca">
          <span className="conversas__busca-lupa" aria-hidden="true">
            ⌕
          </span>
          <input
            type="search"
            className="conversas__busca-campo"
            placeholder={ROTULO_DA_BUSCA_DE_CONVERSAS}
            aria-label={ROTULO_DA_BUSCA_DE_CONVERSAS}
            value={busca}
            onChange={(evento) => {
              aoMudarBusca(evento.target.value)
            }}
          />
        </label>
        {inicioDeConversa}
      </div>
      <div className="conversas__filtros" role="group" aria-label="Filtro da lista">
        <button
          type="button"
          className="conversas__pilula"
          aria-pressed={!soNaoLidas}
          onClick={() => {
            aoMudarFiltro(false)
          }}
        >
          {FILTRO_TODAS}
        </button>
        <button
          type="button"
          className="conversas__pilula"
          aria-pressed={soNaoLidas}
          onClick={() => {
            aoMudarFiltro(true)
          }}
        >
          {FILTRO_NAO_LIDAS}
        </button>
      </div>
      {listaPronta && filtradas.length === 0 && termo !== '' ? (
        <p className="conversas__aviso">{NENHUMA_CONVERSA_NA_BUSCA}</p>
      ) : null}
      {listaPronta && filtradas.length === 0 && termo === '' && soNaoLidas ? (
        <p className="conversas__aviso">{NENHUMA_NAO_LIDA}</p>
      ) : null}
      {houveRecusa ? <Alerta texto={mensagemDoErro(erro)} aoAgir={aoRecarregar} /> : null}
      {carregando ? <p className="conversas__aviso">{CARREGANDO_CONVERSAS}</p> : null}
      {!carregando && !houveRecusa && conversas.length === 0 ? (
        <p className="conversas__aviso">{SEM_CONVERSAS}</p>
      ) : null}
      <ul
        className="conversas__itens"
        aria-labelledby={ID_DO_TITULO_DA_LISTA}
        aria-busy={rotulosACaminho}
      >
        {filtradas.map((conversa) => {
          const contexto = contextoDaConversa(conversa, rotulos)
          const leitura = leituraDa(conversa)
          const naoLida = estaNaoLida(conversa)
          const menuAberto = menuDe === conversa.id
          return (
            <li key={conversa.id} id={idDaLinha(conversa.id)} className="conversas__linha-da-lista">
              <button
                type="button"
                className="conversas__item"
                data-ativa={conversa.id === abertaId ? 'sim' : 'nao'}
                aria-current={conversa.id === abertaId ? 'true' : undefined}
                onClick={() => {
                  aoEscolher(conversa.id)
                }}
                onContextMenu={(evento) => {
                  // O menu do navegador não abre: o da linha é o que a pessoa pediu.
                  evento.preventDefault()
                  quemAbriuOMenu.current = evento.currentTarget
                  setMenuDe(conversa.id)
                }}
              >
                <span className="conversas__avatar" aria-hidden="true">
                  {contexto.iniciais}
                </span>
                <span className="conversas__item-corpo">
                  <span className="conversas__item-topo">
                    <span className="conversas__nome">{contexto.nome}</span>
                    {/* As etiquetas da equipe (13.6), no lugar da tag automática: o nome por
                        extenso é o que o leitor de tela anuncia, e a cor é só pastel. */}
                    <ChipsDaConversa
                      daPessoa={contexto.etiquetasDaPessoa}
                      daEmpresa={contexto.etiquetasDaEmpresa}
                      maximo={ETIQUETAS_NA_LINHA}
                    />
                  </span>
                  {/* A Empresa embaixo do nome (chamado de 21/10, P12), só quando o `cadastro` a
                      entregou e ela não repete o nome. */}
                  {contexto.empresa === undefined ? null : (
                    <span className="conversas__item-empresa">{contexto.empresa}</span>
                  )}
                  <span className="conversas__previa">{previaDaConversa(conversa)}</span>
                </span>
                <span className="conversas__item-lado">
                  <span className="conversas__hora">{horaDaLista(conversa.ultima_mensagem_em)}</span>
                  {/* O contador de não lidas (13.8, DESIGN.md: dourado com número navy), para todos
                      que veem a conversa (D-9) — e não o verde da régua. */}
                  {naoLida ? (
                    <span
                      className="conversas__contador numero"
                      role="img"
                      aria-label={rotuloDoContador(leitura.naoLidas)}
                      title={rotuloDoContador(leitura.naoLidas)}
                    >
                      {textoDoContador(leitura.naoLidas)}
                    </span>
                  ) : null}
                </span>
              </button>
              {/* O "⋯" do celular e da tela de toque (D-9 da 13.8; 13.14c): **fora** do botão da
                  linha — um botão dentro de outro não é HTML válido, e o toque nele abriria a
                  conversa junto. */}
              {comMaisOpcoes ? (
                <button
                  type="button"
                  className="conversas__mais"
                  aria-label={maisOpcoesDe(contexto.nome)}
                  aria-haspopup="menu"
                  aria-expanded={menuAberto}
                  onClick={(evento) => {
                    quemAbriuOMenu.current = evento.currentTarget
                    setMenuDe(menuAberto ? null : conversa.id)
                  }}
                >
                  <span aria-hidden="true">⋯</span>
                </button>
              ) : null}
              {menuAberto ? (
                <div
                  className="conversas__menu-da-linha"
                  role="menu"
                  aria-label={opcoesDe(contexto.nome)}
                  onKeyDown={(evento) => {
                    /* ↑/↓ percorrem os itens, em volta (o padrão de `role="menu"`). */
                    if (evento.key !== 'ArrowDown' && evento.key !== 'ArrowUp') {
                      return
                    }
                    evento.preventDefault()
                    const itens = Array.from(
                      evento.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
                    )
                    const atual = itens.indexOf(document.activeElement as HTMLButtonElement)
                    const passo = evento.key === 'ArrowDown' ? 1 : -1
                    itens[(atual + passo + itens.length) % itens.length]?.focus()
                  }}
                  onBlur={(evento) => {
                    /* O foco saiu do menu (Tab, ou o clique noutro lugar): ele fecha. O "⋯" da
                       própria linha é a exceção — o clique nele já alterna o menu, e fechar aqui
                       o faria reabrir no mesmo clique. */
                    const destino = evento.relatedTarget
                    const paraOMaisDaLinha =
                      destino instanceof HTMLElement &&
                      destino.classList.contains('conversas__mais') &&
                      evento.currentTarget.parentElement?.contains(destino) === true
                    if (!evento.currentTarget.contains(destino) && !paraOMaisDaLinha) {
                      setMenuDe(null)
                    }
                  }}
                >
                  {/* `tabIndex={-1}` nos itens: dentro do menu quem anda são as setas, e o Tab
                      sai dele (e o fecha) — o padrão de `role="menu"` desde que ele ganhou o
                      segundo item (13.8). */}
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className="conversas__item-do-menu"
                    onClick={() => {
                      setMenuDe(null)
                      aoAbrirDados(conversa.id)
                    }}
                  >
                    {DADOS_DO_CONTATO}
                  </button>
                  {/* "Marcar como lida/não lida" (13.8): só para quem lê a conversa (FR-87). Não
                      abre a conversa (D-13), e o foco volta a quem abriu o menu. */}
                  {leitura.podeMarcar ? (
                    <button
                      type="button"
                      role="menuitem"
                      tabIndex={-1}
                      className="conversas__item-do-menu"
                      onClick={() => {
                        setMenuDe(null)
                        aoMarcar(conversa.id, naoLida)
                        quemAbriuOMenu.current?.focus()
                      }}
                    >
                      {naoLida ? MARCAR_COMO_LIDA : MARCAR_COMO_NAO_LIDA}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

interface PropsDoChat {
  conversa: Conversa
  rotulos: RotulosDoCadastro
  ehCelular: boolean
  aoVoltar: () => void
  /** Abre o cartão do próprio número — o remédio que a recusa `409` do envio precisa oferecer. */
  aoAbrirMeuNumero: () => void
  /** Pôr o foco no compositor ao montar: só quando a pessoa abriu a conversa, no computador. */
  focarCompositor: boolean
  /** O número não está conectado: compositor, anexo, soltar e microfone desabilitados. */
  envioBloqueado: boolean
  /** O nome do cabeçalho abre o painel "Dados do contato" (13.3). */
  aoAbrirDados: () => void
  /** Se o painel está aberto sobre esta conversa — o `aria-expanded` do nome. */
  dadosAbertos: boolean
}

/** O `id` da descrição acessível do nome do cabeçalho ("Ver os dados do contato"). */
const ID_DA_DESCRICAO_DO_NOME = 'conversas-nome-do-contato-descricao'

/** Uma linha do chat: o separador de um dia civil, ou uma mensagem. */
type LinhaDoChat =
  | { tipo: 'dia'; dia: string; ocorrida_em: string }
  | { tipo: 'mensagem'; mensagem: Mensagem }

/** As mensagens com um separador antes da primeira de cada dia civil (WhatsApp Web, `.dia`). */
export function comSeparadoresDeDia(mensagens: readonly Mensagem[]): LinhaDoChat[] {
  const linhas: LinhaDoChat[] = []
  let diaAnterior: string | null = null
  for (const mensagem of mensagens) {
    const dia = diaCivilDe(mensagem.ocorrida_em)
    if (dia !== diaAnterior) {
      linhas.push({ tipo: 'dia', dia, ocorrida_em: mensagem.ocorrida_em })
      diaAnterior = dia
    }
    linhas.push({ tipo: 'mensagem', mensagem })
  }
  return linhas
}

/** Um `dataTransfer` traz arquivos? (o arrastar de texto ou de link não abre a prévia). */
function arrastaArquivos(evento: DragEvent): boolean {
  return Array.from(evento.dataTransfer.types).includes('Files')
}

function Chat({
  conversa,
  rotulos,
  ehCelular,
  aoVoltar,
  aoAbrirMeuNumero,
  focarCompositor,
  envioBloqueado,
  aoAbrirDados,
  dadosAbertos,
}: PropsDoChat) {
  const contexto = contextoDaConversa(conversa, rotulos)
  const empresasDoChat = empresasDaConversa(conversa, rotulos)
  /* As Empresas em que quem olha cria Negócio (code review C): as editáveis da Pessoa no lote;
     sem a Pessoa no lote, "não sei" (`undefined`) — e vale o `404` da ficha, como antes. */
  const editaveisDoChat =
    conversa.pessoa_id === null ? undefined : rotulos.editaveisDasPessoas?.get(conversa.pessoa_id)
  /* 13.20c (code review A4): a Pessoa lida só pelo Negócio sem Empresa que quem olha recebeu — sem
     Empresa visível e `pode_editar` falso — não ganha "+ Negócio para esta pessoa": o Negócio dela
     já existe, e o servidor recusaria o contato. Só o `false` explícito esconde. */
  const soPeloRamo =
    conversa.pessoa_id !== null && rotulos.podeEditarDasPessoas?.get(conversa.pessoa_id) === false
  /* 13.20b2 (Y10): os Negócios em andamento e sem Empresa do contato, sob a policy de quem olha. */
  /*
   * A conversa recém-nascida de um número novo (sem mensagem e sem Empresa): o N0 da oportunidade
   * automática nasce segundos depois, pelo barramento — a leitura espera por ele até
   * `PRAZO_DA_ESPERA_DO_N0` depois do nascimento, e só então a ação vira "+ Negócio" (revisão da
   * 13.21; sem isso, o "Iniciar" abria a conversa com "+ Negócio para esta pessoa" e ficava nele).
   */
  const esperarON0 =
    conversa.pessoa_id !== null && conversa.empresa_id === null && conversa.previa === null
      ? Date.parse(conversa.iniciada_em) + PRAZO_DA_ESPERA_DO_N0
      : undefined
  const negociosDoContato = useNegociosDaPessoa(
    conversa.pessoa_id,
    esperarON0 === undefined ? {} : { esperarAte: esperarON0 },
  )
  /* Enquanto espera, o botão fica "carregando" no lugar do "Abrir negócio"; no prazo, a tela
     redesenha uma vez (o relógio da leitura já parou) e decide. */
  const [prazoDoN0Vencido, setPrazoDoN0Vencido] = useState(
    () => esperarON0 === undefined || Date.now() >= esperarON0,
  )
  const esperandoON0 =
    !prazoDoN0Vencido &&
    (negociosDoContato.data === undefined || negociosDoContato.data.length === 0)
  useEffect(() => {
    if (esperarON0 === undefined) {
      return
    }
    const relogio = window.setTimeout(
      () => {
        setPrazoDoN0Vencido(true)
      },
      Math.max(0, esperarON0 - Date.now()),
    )
    return () => {
      window.clearTimeout(relogio)
    }
  }, [esperarON0])
  /*
   * A conversa de outro número (13.11c, FR-71): quem lê — o Responsável da Empresa, a gestão — não
   * envia por ela. Sem compositor, sem anexo, sem áudio, sem reagir; o `403` do servidor continua
   * sendo quem recusa, e esconder é conveniência. `semEnvio` junta isso à faixa do próprio número.
   */
  const deOutroNumero = ehDeOutroNumero(conversa)
  const semEnvio = envioBloqueado || deOutroNumero
  const consulta = useMensagens(conversa.id)
  const envio = useEnviarMensagem(conversa.id)
  const envioDeMidia = useEnviarMidia(conversa.id)
  const reacao = useReagir(conversa.id)
  const [rascunho, setRascunho] = useState('')
  const [bandejaAberta, setBandejaAberta] = useState<string | null>(null)
  const fim = useRef<HTMLDivElement | null>(null)
  const seletor = useRef<HTMLInputElement | null>(null)
  /* O `accept` do seletor muda com o item do menu escolhido; fora do menu, a lista inteira. */
  const [aceita, setAceita] = useState(FORMATOS_ACEITOS)
  const [menuDeAnexos, setMenuDeAnexos] = useState(false)
  const botaoDeAnexar = useRef<HTMLButtonElement | null>(null)
  const menuDeAnexosRef = useRef<HTMLSpanElement | null>(null)
  const compositor = useRef<HTMLInputElement | null>(null)
  const focarAoMontar = useRef(focarCompositor)
  useEffect(() => {
    if (focarAoMontar.current) {
      compositor.current?.focus()
    }
  }, [])
  const razao = envioBloqueado ? ID_DA_FAIXA_DO_NUMERO : undefined

  /* A prévia dos anexos (`AnexosParaEnviar`): o que espera para sair, e qual está à vista. */
  const [anexos, setAnexos] = useState<AnexoPendente[]>([])
  const [anexoSelecionado, setAnexoSelecionado] = useState<string>('')
  const [enviandoAnexos, setEnviandoAnexos] = useState(false)
  const [anexosEnviados, setAnexosEnviados] = useState(0)
  /* O arrastar por cima do chat: um contador, porque `dragenter`/`dragleave` disparam a cada filho. */
  const arrastando = useRef(0)
  const [soltarAberto, setSoltarAberto] = useState(false)
  /* As URLs de prévia são liberadas quando o chat desmonta (troca de conversa remonta por `key`). */
  const anexosVivos = useRef<AnexoPendente[]>([])
  /* A conversa de outro número, para o laço do envio de anexos que já estava no ar (13.11c). */
  const deOutroAgora = useRef(deOutroNumero)
  /*
   * A conversa que **vira** de outro número com ela aberta (a passagem do número, ou o rollback do
   * servidor): o rascunho, a prévia, o menu, a bandeja e a faixa de soltar somem no mesmo render —
   * ajustado no render, e não num efeito, para nada pintar com o "Enviar" à vista. O efeito logo
   * abaixo libera as URLs da prévia: ele é declarado **antes** do que copia `anexos` para
   * `anexosVivos`, e por isso ainda lê os anexos do commit anterior (os efeitos rodam na ordem da
   * declaração).
   */
  const [deOutroAntes, setDeOutroAntes] = useState(deOutroNumero)
  if (deOutroAntes !== deOutroNumero) {
    setDeOutroAntes(deOutroNumero)
    if (deOutroNumero) {
      setAnexos([])
      setAnexoSelecionado('')
      setAnexosEnviados(0)
      setRascunho('')
      setMenuDeAnexos(false)
      setBandejaAberta(null)
      setSoltarAberto(false)
    }
  }
  useEffect(() => {
    deOutroAgora.current = deOutroNumero
    if (deOutroNumero) {
      anexosVivos.current.forEach(liberarAnexo)
      anexosVivos.current = []
      arrastando.current = 0
    }
  }, [deOutroNumero])
  useEffect(() => {
    anexosVivos.current = anexos
  }, [anexos])
  useEffect(
    () => () => {
      anexosVivos.current.forEach(liberarAnexo)
    },
    [],
  )

  const gravacao = useGravacaoDeAudio((audio) => {
    envioDeMidia.mutate({
      arquivo: audio.arquivo,
      tipo: 'audio',
      duracaoEmSegundos: audio.duracaoEmSegundos,
    })
  })
  const podeGravar = navegadorGravaAudio()
  const cancelarAGravacao = gravacao.cancelar
  useEffect(() => {
    if (!deOutroNumero) {
      return
    }
    cancelarAGravacao()
  }, [deOutroNumero, cancelarAGravacao])

  const mensagens: readonly Mensagem[] = consulta.data ?? []

  const ultimaId = mensagens.at(-1)?.id
  useEffect(() => {
    fim.current?.scrollIntoView?.({ block: 'end' })
  }, [ultimaId])

  /**
   * Os arquivos entram na prévia — do seletor ou do soltar —, e nada sobe ainda. O rascunho do
   * compositor vira a legenda do **primeiro** quando a prévia está vazia (o que estava escrito
   * acompanha o anexo, como no WhatsApp); os demais nascem sem legenda.
   */
  const receberArquivos = (lista: FileList | File[]) => {
    const arquivos = Array.from(lista)
    if (arquivos.length === 0 || semEnvio || enviandoAnexos) {
      return
    }
    setAnexos((atuais) => {
      const novos = arquivos.map((arquivo, indice) =>
        anexoPendente(arquivo, atuais.length === 0 && indice === 0 ? rascunho : ''),
      )
      if (atuais.length === 0) {
        setAnexoSelecionado(novos[0]?.id ?? '')
      }
      return [...atuais, ...novos]
    })
  }

  const anexar = (evento: ChangeEvent<HTMLInputElement>) => {
    const arquivos = evento.target.files
    const lista = arquivos === null ? [] : Array.from(arquivos)
    // O campo é zerado sempre: sem isso, escolher **o mesmo** arquivo duas vezes não dispararia o
    // segundo `change`.
    evento.target.value = ''
    receberArquivos(lista)
  }

  const abrirSeletor = (formatos: string) => {
    setAceita(formatos)
    setMenuDeAnexos(false)
    // O `accept` novo precisa estar no DOM antes do clique: o estado só commita no próximo render.
    window.setTimeout(() => {
      seletor.current?.click()
    }, 0)
  }

  const limparAnexos = () => {
    anexos.forEach(liberarAnexo)
    setAnexos([])
    setAnexoSelecionado('')
    setAnexosEnviados(0)
  }

  const removerAnexo = (id: string) => {
    setAnexos((atuais) => {
      const removido = atuais.find((anexo) => anexo.id === id)
      if (removido !== undefined) {
        liberarAnexo(removido)
      }
      const restantes = atuais.filter((anexo) => anexo.id !== id)
      if (anexoSelecionado === id) {
        setAnexoSelecionado(restantes[0]?.id ?? '')
      }
      return restantes
    })
  }

  /**
   * "Enviar" da prévia: um a um, na ordem, esperando cada resposta. A recusa do serviço para a
   * fila **onde parou**: o que saiu sai da prévia, o que não saiu fica nela, e o alerta (verbatim,
   * pelo `Alerta` do envio de mídia) diz por quê.
   */
  const enviarAnexos = async () => {
    if (anexos.length === 0 || enviandoAnexos || semEnvio) {
      return
    }
    setEnviandoAnexos(true)
    setAnexosEnviados(0)
    let saidos = 0
    try {
      for (const anexo of anexos) {
        // A conversa virou de outro número no meio do lote (13.11c): o resto não sai, e a prévia já
        // foi limpa pelo ajuste do render — não há o que repor.
        if (deOutroAgora.current) {
          return
        }
        await envioDeMidia.mutateAsync({
          arquivo: anexo.arquivo,
          tipo: anexo.tipo,
          nome: anexo.arquivo.name,
          legenda: anexo.legenda.trim(),
        })
        saidos += 1
        setAnexosEnviados(saidos)
      }
      limparAnexos()
      setRascunho('')
    } catch {
      anexos.slice(0, saidos).forEach(liberarAnexo)
      if (deOutroAgora.current) {
        return
      }
      const restantes = anexos.slice(saidos)
      setAnexos(restantes)
      setAnexoSelecionado(restantes[0]?.id ?? '')
    } finally {
      setEnviandoAnexos(false)
    }
  }

  /* O menu do clipe: Escape e clique fora fecham, como todo popup. */
  useEffect(() => {
    if (!menuDeAnexos) {
      return
    }
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation()
        setMenuDeAnexos(false)
        botaoDeAnexar.current?.focus()
      }
    }
    const aoTocarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node
      if (
        menuDeAnexosRef.current?.contains(alvo) !== true &&
        botaoDeAnexar.current?.contains(alvo) !== true
      ) {
        setMenuDeAnexos(false)
      }
    }
    document.addEventListener('keydown', aoTeclar)
    document.addEventListener('mousedown', aoTocarFora)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
      document.removeEventListener('mousedown', aoTocarFora)
    }
  }, [menuDeAnexos])
  useEffect(() => {
    if (menuDeAnexos) {
      menuDeAnexosRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus()
    }
  }, [menuDeAnexos])

  const fecharBandeja = (devolverFoco: boolean) => {
    if (bandejaAberta !== null && devolverFoco) {
      document.getElementById(idDoGestoDeReagir(bandejaAberta))?.focus()
    }
    setBandejaAberta(null)
  }

  const alternarBandeja = (mensagemId: string) => {
    if (bandejaAberta !== mensagemId) {
      reacao.reset()
    }
    setBandejaAberta((aberta) => (aberta === mensagemId ? null : mensagemId))
  }

  useEffect(() => {
    if (bandejaAberta === null) {
      return
    }
    const aoTocarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node
      const bandeja = document.getElementById(idDaBandejaDeReacoes(bandejaAberta))
      const gesto = document.getElementById(idDoGestoDeReagir(bandejaAberta))
      if (bandeja?.contains(alvo) !== true && gesto?.contains(alvo) !== true) {
        setBandejaAberta(null)
      }
    }
    document.addEventListener('mousedown', aoTocarFora)
    return () => {
      document.removeEventListener('mousedown', aoTocarFora)
    }
  }, [bandejaAberta])

  const reagirCom = (mensagem: Mensagem, emoji: string) => {
    if (reacao.isPending || deOutroNumero) {
      return
    }
    reacao.mutate({
      mensagemId: mensagem.id,
      emoji: (mensagem.reacao_enviada ?? null) === emoji ? null : emoji,
    })
    fecharBandeja(true)
  }

  const enviar = (evento: FormEvent) => {
    evento.preventDefault()
    const texto = rascunho.trim()
    if (texto === '' || envio.isPending) {
      return
    }
    envio.mutate(texto, {
      // O rascunho só é limpo **no sucesso**: a recusa significa que a mensagem não saiu.
      onSuccess: () => {
        setRascunho('')
      },
    })
  }

  /* O arrastar-e-soltar (WhatsApp Web): o chat inteiro é o alvo, e a faixa "Solte aqui" cobre
     as mensagens enquanto há arquivo por cima. Bloqueado (o número desconectado, ou a conversa de
     outro número), o soltar não faz nada — e o `preventDefault` continua, com `dropEffect` "none":
     sem ele o navegador abriria o arquivo por cima do SPA (13.11c). */
  const aoEntrarArrastando = (evento: DragEvent) => {
    if (!arrastaArquivos(evento) || semEnvio) {
      return
    }
    evento.preventDefault()
    arrastando.current += 1
    setSoltarAberto(true)
  }
  const aoPassarArrastando = (evento: DragEvent) => {
    if (!arrastaArquivos(evento)) {
      return
    }
    evento.preventDefault()
    if (semEnvio) {
      evento.dataTransfer.dropEffect = 'none'
    }
  }
  const aoSairArrastando = (evento: DragEvent) => {
    if (!arrastaArquivos(evento) || semEnvio) {
      return
    }
    arrastando.current = Math.max(0, arrastando.current - 1)
    if (arrastando.current === 0) {
      setSoltarAberto(false)
    }
  }
  const aoSoltar = (evento: DragEvent) => {
    if (!arrastaArquivos(evento)) {
      return
    }
    evento.preventDefault()
    if (semEnvio) {
      return
    }
    arrastando.current = 0
    setSoltarAberto(false)
    receberArquivos(evento.dataTransfer.files)
  }

  const previaAberta = anexos.length > 0
  const anexoAtual = anexos.find((anexo) => anexo.id === anexoSelecionado)?.id ?? anexos[0]?.id ?? ''

  /* Os separadores de dia: um antes da primeira mensagem de cada dia civil. */
  const linhasDoChat = comSeparadoresDeDia(mensagens)

  return (
    <div
      className="conversas__chat"
      data-arrastando={soltarAberto ? 'sim' : 'nao'}
      onDragEnter={aoEntrarArrastando}
      onDragOver={aoPassarArrastando}
      onDragLeave={aoSairArrastando}
      onDrop={aoSoltar}
    >
      <div className="conversas__chat-topo" data-celular={ehCelular ? 'sim' : 'nao'}>
        <span className="conversas__avatar conversas__avatar--chat" aria-hidden="true">
          {contexto.iniciais}
        </span>
        <div className="conversas__chat-identidade">
          {/* O nome é um botão **dentro** do heading (13.3): o heading continua com o nome por
              nome acessível, e o clique abre os dados do contato, como no WhatsApp. */}
          <h2 className="conversas__chat-nome">
            <button
              id={ID_DO_NOME_DO_CONTATO}
              type="button"
              className="conversas__chat-nome-botao"
              aria-describedby={ID_DA_DESCRICAO_DO_NOME}
              aria-expanded={dadosAbertos}
              onClick={aoAbrirDados}
            >
              {contexto.nome}
            </button>
          </h2>
          <span id={ID_DA_DESCRICAO_DO_NOME} className="conversas__so-para-leitor">
            {VER_OS_DADOS_DO_CONTATO}
          </span>
          <span className="conversas__chat-contexto">
            <ChipsDaConversa
              daPessoa={contexto.etiquetasDaPessoa}
              daEmpresa={contexto.etiquetasDaEmpresa}
            />
            <span className="conversas__chat-linha">{contexto.contexto}</span>
          </span>
        </div>
        <div className="conversas__chat-acoes">
          {/* 13.19c2: as Empresas da conversa são as visíveis da Pessoa (`empresasDaConversa`). */}
          {/* 13.20b2 (Y10): o Negócio sem Empresa do contato conta no "Abrir negócio" — com ele,
              a Pessoa solta não ganha "+ Negócio para esta pessoa" (FR-3). */}
          {/* Code review (A2): só o recorte **lido** e vazio vale "+ Negócio"; no ar, o botão fica
              ocupado no mesmo lugar, e a falha é "não sei". Sem Pessoa, o recorte nem sai. */}
          {empresasDoChat.length === 0 &&
          (conversa.pessoa_id === null ||
            (negociosDoContato.isSuccess &&
              negociosDoContato.data.length === 0 &&
              !esperandoON0)) ? (
            soPeloRamo ? null : (
              <NovoNegocioParaAPessoa
                empresaId={null}
                pessoaId={conversa.pessoa_id}
                nome={contexto.nome}
              />
            )
          ) : (
            <AbrirNegocio
              empresaIds={empresasDoChat}
              criaveis={editaveisDoChat}
              nomes={rotulos.empresas}
              doContato={negociosDoContato.data ?? []}
              carregandoDoContato={negociosDoContato.isLoading || esperandoON0}
              falhouDoContato={negociosDoContato.isError}
            />
          )}
          {ehCelular ? (
            <button type="button" className="conversas__voltar" onClick={aoVoltar}>
              {VOLTAR_PARA_A_LISTA}
            </button>
          ) : null}
        </div>
      </div>
      {consulta.isError ? (
        <Alerta
          texto={mensagemDoErro(consulta.error)}
          aoAgir={() => {
            void consulta.refetch()
          }}
        />
      ) : null}
      {envio.isError ? (
        <Alerta
          texto={mensagemDoErro(envio.error)}
          rotuloDaAcao={ABRIR_MEU_NUMERO}
          aoAgir={
            envio.error instanceof ErroDaApi && envio.error.status === STATUS_DE_CONFLITO
              ? aoAbrirMeuNumero
              : undefined
          }
        />
      ) : null}
      {envioDeMidia.isError ? (
        <Alerta
          texto={mensagemDoErro(envioDeMidia.error)}
          rotuloDaAcao={ABRIR_MEU_NUMERO}
          aoAgir={
            envioDeMidia.error instanceof ErroDaApi &&
            envioDeMidia.error.status === STATUS_DE_CONFLITO
              ? aoAbrirMeuNumero
              : undefined
          }
        />
      ) : null}
      {reacao.isError ? (
        <Alerta
          texto={mensagemDoErro(reacao.error)}
          rotuloDaAcao={ABRIR_MEU_NUMERO}
          aoAgir={
            reacao.error instanceof ErroDaApi &&
            reacao.error.status === STATUS_DE_CONFLITO &&
            reacao.error.message === NUMERO_DESCONECTADO_DO_SERVICO
              ? aoAbrirMeuNumero
              : undefined
          }
        />
      ) : null}
      {gravacao.recusa === null ? null : <Alerta texto={gravacao.recusa} />}

      <div className="conversas__corpo">
        <ul
          className="conversas__mensagens"
          data-celular={ehCelular ? 'sim' : 'nao'}
          aria-label={MENSAGENS_DA_CONVERSA}
          aria-live="polite"
          aria-relevant="additions"
        >
          {consulta.isLoading ? <li className="conversas__aviso">{CARREGANDO_MENSAGENS}</li> : null}
          {!consulta.isLoading && !consulta.isError && mensagens.length === 0 ? (
            <li className="conversas__aviso">{SEM_MENSAGENS}</li>
          ) : null}
          {linhasDoChat.map((item) => {
            if (item.tipo === 'dia') {
              return (
                <li key={`dia-${item.dia}`} className="conversas__dia" aria-hidden="true">
                  {rotuloDoDia(item.ocorrida_em)}
                </li>
              )
            }
            const { mensagem } = item
            return (
              <li key={mensagem.id} className="conversas__linha" data-direcao={mensagem.direcao}>
                <div className="conversas__balao" data-direcao={mensagem.direcao}>
                  {/* O gesto de reagir, no canto do balão: aparece ao passar o mouse ou ao focar
                      (protótipo `.balao__acoes`), e a bandeja abre a partir dele. Na conversa de
                      outro número não há gesto: reagir é enviar (13.11c). */}
                  {deOutroNumero ? null : (
                    <span
                      className="conversas__reagir"
                      onKeyDown={(evento) => {
                        if (evento.key === 'Escape' && bandejaAberta === mensagem.id) {
                          evento.stopPropagation()
                          fecharBandeja(true)
                        }
                      }}
                    >
                      <button
                        type="button"
                        id={idDoGestoDeReagir(mensagem.id)}
                        className="conversas__abrir-reacoes"
                        aria-label={REAGIR}
                        title={REAGIR}
                        aria-haspopup="true"
                        aria-expanded={bandejaAberta === mensagem.id}
                        aria-controls={
                          bandejaAberta === mensagem.id ? idDaBandejaDeReacoes(mensagem.id) : undefined
                        }
                        onClick={() => {
                          alternarBandeja(mensagem.id)
                        }}
                      >
                        <span aria-hidden="true">🙂</span>
                      </button>
                      {bandejaAberta === mensagem.id ? (
                        <span
                          id={idDaBandejaDeReacoes(mensagem.id)}
                          className="conversas__bandeja"
                          role="group"
                          aria-label={ESCOLHA_UMA_REACAO}
                        >
                          {EMOJIS_DE_REACAO.map((emoji) => {
                            const atual = (mensagem.reacao_enviada ?? null) === emoji
                            return (
                              <button
                                key={emoji}
                                type="button"
                                className="conversas__emoji"
                                aria-pressed={atual}
                                aria-label={
                                  atual ? rotuloDeRemoverAReacao(emoji) : rotuloDeReagirCom(emoji)
                                }
                                disabled={reacao.isPending}
                                onClick={() => {
                                  reagirCom(mensagem, emoji)
                                }}
                              >
                                <span aria-hidden="true">{emoji}</span>
                              </button>
                            )
                          })}
                        </span>
                      ) : null}
                    </span>
                  )}
                  <MidiaDoBalao mensagem={mensagem} />
                  {mensagem.texto === null ? null : (
                    <span className="conversas__texto">{mensagem.texto}</span>
                  )}
                  <span className="conversas__balao-rodape">
                    <span className="conversas__hora-do-balao">
                      {horaDoBalao(mensagem.ocorrida_em)}
                    </span>
                    {mensagem.direcao === 'enviada' ? (
                      <span className="conversas__enviada" role="img" aria-label={ENVIADA}>
                        ✓
                      </span>
                    ) : null}
                  </span>
                  <ReacoesDoBalao mensagem={mensagem} deOutro={deOutroNumero} />
                </div>
              </li>
            )
          })}
          <div ref={fim} />
        </ul>
        {soltarAberto ? (
          <div className="conversas__soltar" role="status">
            <span className="conversas__soltar-caixa">{SOLTE_AQUI}</span>
          </div>
        ) : null}
        {previaAberta && !deOutroNumero ? (
          <AnexosParaEnviar
            anexos={anexos}
            selecionadoId={anexoAtual}
            enviando={enviandoAnexos}
            enviados={anexosEnviados}
            aoSelecionar={setAnexoSelecionado}
            aoMudarLegenda={(id, legenda) => {
              setAnexos((atuais) =>
                atuais.map((anexo) => (anexo.id === id ? { ...anexo, legenda } : anexo)),
              )
            }}
            aoRemover={removerAnexo}
            aoAdicionarMais={() => {
              abrirSeletor(FORMATOS_ACEITOS)
            }}
            aoEnviar={() => {
              void enviarAnexos()
            }}
            aoFechar={limparAnexos}
          />
        ) : null}
      </div>

      {deOutroNumero ? (
        /* No lugar do compositor (13.11c, FR-71): a nota, e nenhum caminho de envio. `role="note"`,
           e não `status`: ela não é um acontecimento a anunciar, é o que a conversa é. */
        <p className="conversas__de-outro-numero" role="note">
          {CONVERSA_DE_OUTRO_NUMERO}
        </p>
      ) : (
        <form className="conversas__escrever" onSubmit={enviar} data-previa={previaAberta ? 'sim' : 'nao'}>
          <input
            ref={seletor}
            className="conversas__seletor"
            type="file"
            accept={aceita}
            multiple
            hidden
            aria-hidden="true"
            tabIndex={-1}
            onChange={anexar}
          />
          {gravacao.gravando ? (
            <>
              <span className="conversas__gravando" role="status">
                <span className="conversas__gravando-marca" aria-hidden="true" />
                {GRAVANDO} · {formatarDuracao(gravacao.segundos) ?? '0:00'}
              </span>
              <button
                type="button"
                className="conversas__cancelar-gravacao"
                onClick={gravacao.cancelar}
              >
                {CANCELAR_GRAVACAO}
              </button>
              <button type="button" className="conversas__enviar-texto" onClick={gravacao.terminar}>
                {PARAR_E_ENVIAR}
              </button>
            </>
          ) : (
            <>
              <span className="conversas__anexar-ancora">
                <button
                  ref={botaoDeAnexar}
                  type="button"
                  className="conversas__acao-de-midia"
                  aria-label={ANEXAR}
                  title={ANEXAR}
                  aria-haspopup="menu"
                  aria-expanded={menuDeAnexos}
                  aria-describedby={razao}
                  disabled={envioDeMidia.isPending || envioBloqueado || previaAberta}
                  onClick={() => {
                    setMenuDeAnexos((aberto) => !aberto)
                  }}
                >
                  <span aria-hidden="true">📎</span>
                </button>
                {menuDeAnexos ? (
                  <span
                    ref={menuDeAnexosRef}
                    className="conversas__menu-de-anexos"
                    role="menu"
                    aria-label={MENU_DE_ANEXOS}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="conversas__item-do-menu"
                      onClick={() => {
                        abrirSeletor(ACEITA_FOTOS_E_VIDEOS)
                      }}
                    >
                      <span aria-hidden="true">🖼 </span>
                      {ANEXAR_FOTOS_E_VIDEOS}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="conversas__item-do-menu"
                      onClick={() => {
                        abrirSeletor(ACEITA_DOCUMENTOS)
                      }}
                    >
                      <span aria-hidden="true">📄 </span>
                      {ANEXAR_DOCUMENTO}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="conversas__item-do-menu"
                      onClick={() => {
                        abrirSeletor(ACEITA_AUDIO)
                      }}
                    >
                      <span aria-hidden="true">🎧 </span>
                      {ANEXAR_AUDIO}
                    </button>
                  </span>
                ) : null}
              </span>
              <input
                ref={compositor}
                className="conversas__campo"
                type="text"
                disabled={envioBloqueado || previaAberta}
                aria-describedby={razao}
                value={rascunho}
                maxLength={TAMANHO_MAXIMO_DO_TEXTO}
                placeholder={RASCUNHO}
                aria-label={RASCUNHO}
                onChange={(evento) => {
                  setRascunho(evento.target.value)
                }}
              />
              {podeGravar ? (
                <button
                  type="button"
                  className="conversas__acao-de-midia"
                  aria-label={GRAVAR_AUDIO}
                  title={GRAVAR_AUDIO}
                  aria-describedby={razao}
                  disabled={envioDeMidia.isPending || envioBloqueado || previaAberta}
                  onClick={() => {
                    void gravacao.comecar()
                  }}
                >
                  <span aria-hidden="true">🎙</span>
                </button>
              ) : null}
              <button
                type="submit"
                className="conversas__enviar"
                aria-label={envio.isPending || envioDeMidia.isPending ? ENVIANDO : ENVIAR}
                title={envio.isPending || envioDeMidia.isPending ? ENVIANDO : ENVIAR}
                aria-describedby={razao}
                disabled={
                  envio.isPending ||
                  envioDeMidia.isPending ||
                  envioBloqueado ||
                  previaAberta ||
                  rascunho.trim() === ''
                }
              >
                <span aria-hidden="true">➤</span>
              </button>
            </>
        )}
      </form>
      )}
    </div>
  )
}

export interface PropsDeConversas {
  /** As conversas que a RLS devolveu — a rota é quem as consulta, como em `funil.tsx`. */
  conversas: readonly Conversa[]
  /** Se a lista ainda está no ar. */
  carregando: boolean
  /** A recusa da lista, quando houve — ela desce para a tela verbatim. */
  erro: unknown
  /** Relê a lista. */
  aoRecarregar: () => void
  /** A conversa aberta, resolvida pela rota contra a lista. */
  conversaAberta: Conversa | undefined
  /** A URL nomeou uma conversa que **não está** na lista, e a tela abriu outra. */
  linkNaoEncontrado?: boolean
  /** Escreve o `?conversa=` — a URL é a dona do "onde eu estou". */
  aoEscolherConversa: (conversaId: string) => void
  /** A URL **chegou** com `?conversa=`: no celular ela abre o chat em vez da lista. */
  conversaNaUrl?: boolean
  /**
   * 13.14c (DW-380): no celular, a URL diz que o painel "Dados do contato" está aberto (`?dados=1`,
   * empurrado por esta aba). O painel continua estado da tela; a URL só o abre e o fecha — é assim
   * que o "voltar" do Android o fecha sem sair do WhatsApp.
   */
  dadosNaUrl?: boolean
  /** Empurra a entrada do painel (no celular), depois da da conversa quando ela muda. */
  aoAbrirDadosNaUrl?: (conversaId: string) => void
  /** Tira a entrada do painel: volta no histórico quando ela foi empurrada aqui. */
  aoFecharDadosNaUrl?: () => void
}

export function Conversas({
  conversas,
  carregando,
  erro,
  aoRecarregar,
  conversaAberta,
  linkNaoEncontrado = false,
  aoEscolherConversa,
  conversaNaUrl = false,
  dadosNaUrl = false,
  aoAbrirDadosNaUrl,
  aoFecharDadosNaUrl,
}: PropsDeConversas) {
  const ehCelular = useEhCelular()
  const navegar = useNavigate()
  const irParaIntegracoes = () => {
    void navegar({ to: CAMINHOS.integracoes })
  }

  /* No celular, qual das duas colunas está na tela. Estado local de propósito: o "voltar para a
     lista" não desescolhe a conversa — a URL continua apontando para ela. */
  const [mostrandoLista, setMostrandoLista] = useState(!conversaNaUrl)

  /* O foco no compositor é a pedido: vira `true` quando a pessoa escolhe uma conversa na lista, e só
     no computador. */
  const [focarAoAbrir, setFocarAoAbrir] = useState(false)

  const numero = useSituacaoDoMeuNumero()
  const desconectado = numero.data?.situacao === 'desconectado'
  const pareando = numero.data?.situacao === 'pareando'
  const numeroProprio = (numero.data as { numero?: string | null } | undefined)?.numero ?? null

  /* Um pedido em lote para a tela inteira, e não um por linha (AD-25). */
  const idsDePessoas = useMemo(
    () => [
      ...new Set(
        conversas.map((conversa) => conversa.pessoa_id).filter((id): id is string => id !== null),
      ),
    ],
    [conversas],
  )
  const dePessoas = useNomesDePessoas(idsDePessoas)
  const { data: sessao } = useSessao()
  const gestao = sessao !== undefined && ehDeGestao(sessao.escopo_de_carteira)
  /* Os mapas do lote **estáveis** (code review da 13.19c2, E8): enquanto um lote novo viaja (uma
     conversa nova na lista muda o lote), o que já se sabia continua na tela — nada pisca. */
  const nomesDasPessoas = useMapaEstavel(dePessoas.nomes, dePessoas.carregando)
  const etiquetasDasPessoas = useMapaEstavel(dePessoas.etiquetas, dePessoas.carregando)
  const empresasDasPessoas = useMapaEstavel(dePessoas.empresas, dePessoas.carregando)
  const editaveisDasPessoas = useMapaEstavel(dePessoas.editaveis, dePessoas.carregando)
  const podeEditarDasPessoas = useMapaEstavel(dePessoas.podeEditar, dePessoas.carregando)
  /* 13.19c2: os nomes de Empresa que a tela mostra — o `empresa_id` de cada conversa e, para a
     gestão (a única que vê na conversa as Empresas do `cadastro`, code review A), as visíveis de
     cada Pessoa do lote. Ordenados e sem repetição: o mesmo pedido não muda de chave à toa. */
  const idsDeEmpresas = useMemo(
    () =>
      [
        ...new Set([
          ...conversas
            .map((conversa) => conversa.empresa_id)
            .filter((id): id is string => id !== null),
          ...(gestao ? [...empresasDasPessoas.values()].flat() : []),
        ]),
      ].sort(),
    [conversas, empresasDasPessoas, gestao],
  )
  const deEmpresas = useNomesDeEmpresas(idsDeEmpresas)
  const nomesDasEmpresas = useMapaEstavel(deEmpresas.nomes, deEmpresas.carregando)
  const razoesDasEmpresas = useMapaEstavel(deEmpresas.razoesSociais, deEmpresas.carregando)
  const etiquetasDasEmpresas = useMapaEstavel(deEmpresas.etiquetas, deEmpresas.carregando)

  const rotulosACaminho = dePessoas.carregando || deEmpresas.carregando
  const semNomes = dePessoas.falhou || deEmpresas.falhou

  const rotulos: RotulosDoCadastro = useMemo(
    () => ({
      pessoas: nomesDasPessoas,
      empresas: nomesDasEmpresas,
      razoesDasEmpresas,
      etiquetasDasPessoas,
      etiquetasDasEmpresas,
      empresasDasPessoas,
      editaveisDasPessoas,
      podeEditarDasPessoas,
      gestao,
    }),
    [
      nomesDasPessoas,
      nomesDasEmpresas,
      razoesDasEmpresas,
      etiquetasDasPessoas,
      etiquetasDasEmpresas,
      empresasDasPessoas,
      editaveisDasPessoas,
      podeEditarDasPessoas,
      gestao,
    ],
  )

  /*
   * O painel "Dados do contato" (13.3) mora **aqui**, guardado pelo `conversaId`: quando a conversa
   * aberta deixa de ser aquela — por troca ou por queda da lista —, ele fecha. O aviso do vínculo
   * também mora aqui, e não no painel: se a conversa sair da lista (FR-39), ele continua no topo da
   * página dizendo por quê.
   */
  const modoDoPainel = useModoDoPainel(ehCelular)
  const [painelDe, setPainelDe] = useState<string | null>(null)
  const [avisoDoVinculo, setAvisoDoVinculo] = useState<{
    conversaId: string
    nomeDaEmpresa: string
  } | null>(null)
  const devolverFocoAoNome = useRef(false)

  /*
   * --- A leitura (13.8, FR-87) ---
   *
   * O filtro "Não lidas" e a busca moram aqui, e não na lista: no celular a lista sai da árvore
   * quando o chat abre, e voltar a ela não pode zerar o que a pessoa escolheu.
   */
  const clienteDeConsultas = useQueryClient()
  /*
   * O `empresa_id` da conversa que muda entre duas leituras (code review da 13.19c2, E7) — a réplica
   * recebeu um vínculo: o lote de Pessoas que contém aquela Pessoa é relido, e o painel e a linha
   * não ficam com as Empresas de antes por cinco minutos.
   */
  const empresasVistas = useRef(new Map<string, string | null>())
  useEffect(() => {
    const mudaram = conversas.filter((conversa) => {
      const antes = empresasVistas.current.get(conversa.id)
      return antes !== undefined && antes !== conversa.empresa_id && conversa.pessoa_id !== null
    })
    empresasVistas.current = new Map(conversas.map((conversa) => [conversa.id, conversa.empresa_id]))
    const pessoas = new Set(mudaram.map((conversa) => conversa.pessoa_id as string))
    if (pessoas.size === 0) {
      return
    }
    void clienteDeConsultas.invalidateQueries({
      predicate: (consulta) => {
        const [servico, recurso, lote] = consulta.queryKey
        return (
          servico === 'cadastro' &&
          recurso === 'pessoas' &&
          typeof lote === 'string' &&
          lote.split(',').some((id) => pessoas.has(id))
        )
      },
    })
  }, [conversas, clienteDeConsultas])
  const marcar = useMarcarLeitura()
  /*
   * --- "Iniciar nova conversa" (13.21b, FR-91) ---
   *
   * O lápis só aparece para quem tem `enviar_mensagem` (FR-61-A) — conveniência: quem recusa é o
   * serviço —, e fora de `conectado` fica travado, apontando a faixa de sempre.
   */
  const podeIniciar = sessao !== undefined && PERMISSAO_DE_INICIAR_CONVERSA in sessao.permissoes
  const [iniciando, setIniciando] = useState(false)
  const devolverFocoAoLapis = useRef(false)
  useEffect(() => {
    if (!iniciando && devolverFocoAoLapis.current) {
      devolverFocoAoLapis.current = false
      /* O lápis, quando ele ainda pode receber o foco; senão — travado porque o número caiu com o
         diálogo aberto, ou fora da árvore —, a busca ao lado, que é um alvo seguro (revisão). */
      const lapis = document.querySelector<HTMLButtonElement>('[data-iniciar-nova-conversa]')
      lapis?.focus()
      if (lapis === null || document.activeElement !== lapis) {
        document.querySelector<HTMLInputElement>('.conversas__busca-campo')?.focus()
      }
    }
  }, [iniciando])
  const [soNaoLidas, setSoNaoLidas] = useState(false)
  const [busca, setBusca] = useState('')
  const [mantidasNoFiltro, setMantidasNoFiltro] = useState<ReadonlySet<string>>(() => new Set())
  /*
   * `leituraDe` é a conversa que a pessoa **abriu por gesto** (o clique na linha, ou "Dados do
   * contato" do menu): é só ela que a tela marca de novo quando chega mensagem (D-12). A abertura
   * automática, o `?conversa=`, o "voltar" e o "←" do celular não a preenchem — e a zeram.
   */
  const [leituraDe, setLeituraDe] = useState<string | null>(null)
  /* As conversas cuja marca falhou desde o último gesto: nenhuma remarcação automática nelas. */
  const [falhouDe, setFalhouDe] = useState<ReadonlySet<string>>(() => new Set())
  const [erroDaMarca, setErroDaMarca] = useState<{
    erro: unknown
    conversaId: string
    lida: boolean
  } | null>(null)
  /* A marca em voo por conversa, e o carimbo da última remarcação automática de cada uma. */
  const emVoo = useRef(new Set<string>())
  const remarcadaEm = useRef(new Map<string, string>())
  /* A última intenção pedida enquanto outra marca da mesma conversa estava no ar. */
  const pendentes = useRef(new Map<string, { lida: boolean; porGesto: boolean }>())
  /* Um tique que reavalia a remarcação quando a janela ganha foco ou a aba volta. */
  const [tiqueDoFoco, setTiqueDoFoco] = useState(0)

  /*
   * O painel fecha quando a conversa aberta deixa de ser a dele — a troca (a que estava com ele
   * aberto deixou de ser a aberta) ou a queda da lista (a conversa que deixou de ser lida por quem
   * olha, FR-39). Ajustado no render, com a conversa anterior guardada em estado, e não num efeito: o
   * painel nunca chega a pintar sobre a conversa errada. Abrir pelo menu de **outra** linha não
   * conta — ali a aberta de antes não era a do painel.
   */
  const [abertaAntes, setAbertaAntes] = useState(conversaAberta?.id)
  if (abertaAntes !== conversaAberta?.id) {
    setAbertaAntes(conversaAberta?.id)
    if (painelDe !== null && abertaAntes === painelDe) {
      setPainelDe(null)
    }
    /* A conversa aberta mudou sem ser pelo gesto que a escolheu (o "voltar", a correção da rota,
       a conversa que saiu da lista): a leitura de antes não acompanha (13.8). */
    if (leituraDe !== null && leituraDe !== conversaAberta?.id) {
      setLeituraDe(null)
    }
  }
  if (painelDe !== null && !carregando && !conversas.some((conversa) => conversa.id === painelDe)) {
    setPainelDe(null)
  }
  /*
   * 13.14c (DW-380): no celular, **a URL é quem diz** se o painel está na tela (`?dados=1`, empurrado
   * pela rota): abrir empurra a entrada, o "voltar" do Android a tira e o painel sai — a conversa
   * fica. No computador continua o estado da tela (`painelDe`), como antes.
   */
  const painelPelaUrl = ehCelular && aoAbrirDadosNaUrl !== undefined
  const painelVisivel = painelPelaUrl
    ? dadosNaUrl && conversaAberta !== undefined
    : painelDe !== null && conversaAberta?.id === painelDe
  /* O painel que sai pela URL (o "voltar") devolve o foco ao nome do cabeçalho, como o "Fechar". */
  const visivelAntes = useRef(painelVisivel)
  useEffect(() => {
    const antes = visivelAntes.current
    visivelAntes.current = painelVisivel
    if (painelPelaUrl && antes && !painelVisivel) {
      document.getElementById(ID_DO_NOME_DO_CONTATO)?.focus()
    }
  })

  /* "Fechar" (ou "← Conversa") devolve o foco ao nome do cabeçalho — depois de o chat voltar à
     árvore, que no celular só acontece no render seguinte. */
  useEffect(() => {
    /* Só com o painel já fora da tela: no celular o chat volta à árvore um render depois (e o
       "voltar" do Android, 13.14c, fecha o painel por efeito). */
    if (devolverFocoAoNome.current && !painelVisivel) {
      devolverFocoAoNome.current = false
      document.getElementById(ID_DO_NOME_DO_CONTATO)?.focus()
    }
  })

  /* Manda a marca, sem esperar: o otimista já apagou o contador, e a falha volta como alerta. */
  const enviarMarca = (conversaId: string, lida: boolean, { porGesto = true } = {}) => {
    /*
     * Uma marca por conversa no ar: a segunda espera a primeira, e só a **última** intenção sai
     * depois (clicar "lida" e logo "não lida" não pode terminar em "lida" por ordem de chegada).
     */
    if (emVoo.current.has(conversaId)) {
      pendentes.current.set(conversaId, { lida, porGesto })
      return
    }
    emVoo.current.add(conversaId)
    if (porGesto) {
      setErroDaMarca(null)
    }
    if (lida && soNaoLidas) {
      setMantidasNoFiltro((antes) => new Set(antes).add(conversaId))
    }
    marcar
      .mutateAsync({ conversaId, lida })
      .then(() => {
        setFalhouDe((antes) => semA(antes, conversaId))
      })
      .catch((erro: unknown) => {
        // A falha da remarcação automática só desliga a remarcação; quem vê o alerta é quem fez
        // o gesto (com "Tentar de novo").
        if (porGesto) {
          setErroDaMarca({ erro, conversaId, lida })
        }
        setFalhouDe((antes) => new Set(antes).add(conversaId))
      })
      .finally(() => {
        emVoo.current.delete(conversaId)
        const pendente = pendentes.current.get(conversaId)
        if (pendente !== undefined) {
          pendentes.current.delete(conversaId)
          enviarMarca(conversaId, pendente.lida, { porGesto: pendente.porGesto })
        }
      })
  }

  /* O gesto que abre a conversa (D-11): ela passa a ser lida por esta tela, e marca se não lida. */
  const abrirPorGesto = (conversaId: string) => {
    setLeituraDe(conversaId)
    setFalhouDe((antes) => semA(antes, conversaId))
    const conversa = conversas.find((uma) => uma.id === conversaId)
    if (conversa !== undefined && leituraDa(conversa).podeMarcar && estaNaoLida(conversa)) {
      enviarMarca(conversaId, true)
    }
  }

  /*
   * O anúncio da marca (13.14c, DW-428): uma região `role="status"` só para leitor, montada vazia
   * desde o início (a região que nasce com o texto não é lida), na página — no celular a lista sai
   * da árvore. É dito no gesto, como o contador muda (otimista); a falha continua no alerta. Limpar e
   * repor faz o mesmo texto ser lido de novo.
   */
  const [anuncioDaLeitura, setAnuncioDaLeitura] = useState('')
  const reposicaoDoAnuncio = useRef<number | undefined>(undefined)
  useEffect(
    () => () => {
      window.clearTimeout(reposicaoDoAnuncio.current)
    },
    [],
  )
  const anunciarAMarca = (conversaId: string, lida: boolean) => {
    const conversa = conversas.find((uma) => uma.id === conversaId)
    if (conversa === undefined) {
      return
    }
    const texto = conversaMarcadaComo(contextoDaConversa(conversa, rotulos).nome, lida)
    setAnuncioDaLeitura('')
    window.clearTimeout(reposicaoDoAnuncio.current)
    reposicaoDoAnuncio.current = window.setTimeout(() => {
      setAnuncioDaLeitura(texto)
    }, PAUSA_DO_ANUNCIO_EM_MS)
  }

  /* "Marcar como lida/não lida" do menu (D-13): não abre; "não lida" desliga a remarcação. */
  const marcarPeloMenu = (conversaId: string, lida: boolean) => {
    if (!lida && leituraDe === conversaId) {
      setLeituraDe(null)
    }
    enviarMarca(conversaId, lida)
    anunciarAMarca(conversaId, lida)
  }

  const mudarFiltro = (filtrar: boolean) => {
    setSoNaoLidas(filtrar)
    setMantidasNoFiltro(new Set())
  }

  const escolher = (conversaId: string, { navegar = true } = {}) => {
    setMostrandoLista(false)
    setFocarAoAbrir(!ehCelular)
    // D-16: abrir outra conversa fecha o painel (a edição não salva se perde).
    if (conversaId !== painelDe) {
      setPainelDe(null)
    }
    if (avisoDoVinculo !== null && avisoDoVinculo.conversaId !== conversaId) {
      setAvisoDoVinculo(null)
    }
    if (navegar) {
      aoEscolherConversa(conversaId)
    }
  }

  const abrirDados = (conversaId: string) => {
    /* No celular a rota empurra as duas entradas (a da conversa, se mudou, e a do painel), em
       ordem: escolher aqui empurraria a da conversa duas vezes (13.14c). */
    if (conversaId !== conversaAberta?.id) {
      escolher(conversaId, { navegar: !painelPelaUrl })
    }
    // O foco vai ao título do painel, e não ao compositor do chat que abre junto.
    setFocarAoAbrir(false)
    setMostrandoLista(false)
    if (painelPelaUrl) {
      aoAbrirDadosNaUrl?.(conversaId)
      return
    }
    setPainelDe(conversaId)
  }

  const fecharDados = () => {
    if (painelPelaUrl) {
      //: O foco volta ao nome pelo efeito do painel que sai (acima).
      aoFecharDadosNaUrl?.()
      return
    }
    devolverFocoAoNome.current = true
    setPainelDe(null)
  }

  /* O aviso do vínculo cuja conversa não está mais na lista: ele sobe ao topo da página. */
  const vinculoForaDaLista =
    avisoDoVinculo !== null &&
    !carregando &&
    !conversas.some((conversa) => conversa.id === avisoDoVinculo.conversaId)

  /*
   * A remarcação (D-12, a regra do Always da spec): a conversa aberta por gesto, com o chat na tela,
   * a aba visível e a janela em foco, que recebeu mensagem nova e não foi marcada não lida (aqui ou
   * noutra aba), com as mensagens carregadas — uma marca por chegada, nenhuma com outra em voo nem
   * depois de uma falha.
   */
  const chatNaTela = ehCelular
    ? !mostrandoLista && !painelVisivel
    : !(painelVisivel && modoDoPainel === 'gaveta')
  /* Só quem pode remarcar observa as mensagens daqui (o chat já as lê): sem chat na tela, nada. */
  const mensagensDaAberta = useMensagens(
    chatNaTela && conversaAberta !== undefined && leituraDe === conversaAberta.id
      ? conversaAberta.id
      : undefined,
  )
  useEffect(() => {
    if (conversaAberta === undefined || leituraDe !== conversaAberta.id || !chatNaTela) {
      return
    }
    if (document.visibilityState !== 'visible' || !document.hasFocus()) {
      return
    }
    const { naoLidas, marcada, podeMarcar } = leituraDa(conversaAberta)
    const id = conversaAberta.id
    if (!podeMarcar || naoLidas <= 0 || marcada) {
      return
    }
    if (!mensagensDaAberta.isSuccess || emVoo.current.has(id) || falhouDe.has(id)) {
      return
    }
    if (remarcadaEm.current.get(id) === conversaAberta.ultima_mensagem_em) {
      return
    }
    remarcadaEm.current.set(id, conversaAberta.ultima_mensagem_em)
    enviarMarca(id, true, { porGesto: false })
    // `enviarMarca` muda a cada render e só lê estado de agora: o efeito reage ao que decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversaAberta, leituraDe, chatNaTela, mensagensDaAberta.isSuccess, falhouDe, tiqueDoFoco])

  /* A janela que ganha foco e a aba que volta reavaliam a remarcação. */
  useEffect(() => {
    const reavaliar = () => {
      setTiqueDoFoco((tique) => tique + 1)
    }
    window.addEventListener('focus', reavaliar)
    document.addEventListener('visibilitychange', reavaliar)
    return () => {
      window.removeEventListener('focus', reavaliar)
      document.removeEventListener('visibilitychange', reavaliar)
    }
  }, [])

  /* O total do menu acompanha os contadores que quem olha lê (D-16): mudou, relê. */
  const assinaturaDasNaoLidas = conversas
    .filter((conversa) => leituraDa(conversa).podeMarcar)
    .map((conversa) => {
      const { naoLidas, marcada } = leituraDa(conversa)
      return `${conversa.id}:${String(naoLidas)}:${String(marcada)}`
    })
    .join('|')
  const assinaturaAnterior = useRef<string | null>(null)
  useEffect(() => {
    if (carregando) {
      return
    }
    /* A primeira assinatura não é mudança: o shell acabou de ler o total. */
    if (assinaturaAnterior.current !== null && assinaturaAnterior.current !== assinaturaDasNaoLidas) {
      void clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_TOTAL_DE_NAO_LIDAS })
    }
    assinaturaAnterior.current = assinaturaDasNaoLidas
  }, [assinaturaDasNaoLidas, carregando, clienteDeConsultas])

  const lista = (
    <ListaDeConversas
      conversas={conversas}
      rotulos={rotulos}
      rotulosACaminho={rotulosACaminho}
      abertaId={conversaAberta?.id}
      carregando={carregando}
      erro={erro}
      ehCelular={ehCelular}
      aoEscolher={(conversaId) => {
        escolher(conversaId)
        abrirPorGesto(conversaId)
      }}
      aoAbrirDados={(conversaId) => {
        abrirDados(conversaId)
        abrirPorGesto(conversaId)
      }}
      aoRecarregar={aoRecarregar}
      soNaoLidas={soNaoLidas}
      aoMudarFiltro={mudarFiltro}
      busca={busca}
      aoMudarBusca={setBusca}
      mantidasNoFiltro={
        leituraDe === null ? mantidasNoFiltro : new Set([...mantidasNoFiltro, leituraDe])
      }
      aoMarcar={marcarPeloMenu}
      inicioDeConversa={
        podeIniciar ? (
          <BotaoDeIniciarConversa
            aoAbrir={() => {
              setIniciando(true)
            }}
            travadoPor={desconectado || pareando ? ID_DA_FAIXA_DO_NUMERO : undefined}
          />
        ) : null
      }
    />
  )

  const chat =
    conversaAberta === undefined ? (
      <div className="conversas__chat conversas__chat--vazio">
        <p className="conversas__aviso">
          {carregando ? CARREGANDO_CONVERSAS : SEM_CONVERSA_ABERTA}
        </p>
      </div>
    ) : (
      <Chat
        // A remontagem por `key` zera o rascunho e a prévia ao trocar de conversa.
        key={conversaAberta.id}
        conversa={conversaAberta}
        rotulos={rotulos}
        ehCelular={ehCelular}
        aoVoltar={() => {
          setMostrandoLista(true)
          // O "←" do celular: a conversa sai da tela, e a remarcação com ela (13.8).
          setLeituraDe(null)
          // No celular, voltar à lista fecha o painel: reabrir a conversa traz o chat, e não ele.
          setPainelDe(null)
        }}
        aoAbrirMeuNumero={irParaIntegracoes}
        focarCompositor={focarAoAbrir}
        envioBloqueado={desconectado || pareando}
        aoAbrirDados={() => {
          abrirDados(conversaAberta.id)
        }}
        dadosAbertos={painelVisivel}
      />
    )

  const painel =
    painelVisivel && conversaAberta !== undefined ? (
      <DadosDoContato
        // Chave própria: o chat irmão já usa o `id` da conversa como chave.
        key={`painel-${conversaAberta.id}`}
        conversa={conversaAberta}
        modo={modoDoPainel}
        {...(avisoDoVinculo !== null && avisoDoVinculo.conversaId === conversaAberta.id
          ? { avisoDoVinculo: contatoVinculadoA(avisoDoVinculo.nomeDaEmpresa) }
          : {})}
        aoFechar={fecharDados}
        bloquearOVoltar={ehCelular}
        aoVincular={(nomeDaEmpresa) => {
          setAvisoDoVinculo({ conversaId: conversaAberta.id, nomeDaEmpresa })
        }}
      />
    ) : null

  return (
    <section className="conversas__pagina" aria-label={TITULO_DA_LISTA}>
      <p className="conversas__so-para-leitor" role="status" data-anuncio-da-leitura="">
        {anuncioDaLeitura}
      </p>
      {/* O próprio número no cabeçalho da página (protótipo v2: "seu número: …") e o caminho até
          Integrações, onde ele se conecta, desconecta e pareia (11.2). */}
      <AcoesDoCabecalho>
        {numeroProprio === null ? null : (
          <span className="conversas__numero-proprio">
            {SEU_NUMERO} {formatarNumeroDeWhatsapp(numeroProprio)}
          </span>
        )}
        <Link className="conversas__meu-numero" to={CAMINHOS.integracoes}>
          {ABRIR_MEU_NUMERO}
        </Link>
      </AcoesDoCabecalho>
      {desconectado || pareando ? (
        <p
          id={ID_DA_FAIXA_DO_NUMERO}
          className="conversas__situacao-do-numero"
          role="status"
          aria-label={desconectado ? WHATSAPP_DESCONECTADO : WHATSAPP_PAREANDO}
          data-situacao={desconectado ? 'desconectado' : 'pareando'}
        >
          <span>{desconectado ? FRASE_DO_DESCONECTADO : NUMERO_PAREANDO}</span>
          <Link className="conversas__tentar" to={CAMINHOS.integracoes}>
            {ABRIR_INTEGRACOES}
          </Link>
        </p>
      ) : null}
      {linkNaoEncontrado ? <Alerta texto={CONVERSA_DO_LINK_NAO_ESTA_NA_LISTA} /> : null}
      {erroDaMarca !== null ? (
        <Alerta
          texto={mensagemDoErro(erroDaMarca.erro)}
          aoAgir={() => {
            enviarMarca(erroDaMarca.conversaId, erroDaMarca.lida)
          }}
        />
      ) : null}
      {semNomes ? <Alerta texto={NOMES_INDISPONIVEIS} /> : null}
      {iniciando ? (
        <DialogoDeIniciarConversa
          ehCelular={ehCelular}
          aoFechar={() => {
            devolverFocoAoLapis.current = true
            setIniciando(false)
          }}
          aoIniciar={(conversa) => {
            setIniciando(false)
            //: A conversa aberta como a do clique na lista: a URL, o chat e o foco no compositor.
            escolher(conversa.id)
            abrirPorGesto(conversa.id)
          }}
        />
      ) : null}
      {vinculoForaDaLista ? (
        <p className="conversas__vinculo-fora-da-lista" role="status">
          <span>{contatoVinculadoA(avisoDoVinculo.nomeDaEmpresa)}</span>{' '}
          <span>{CONVERSA_SAIU_DA_LISTA}</span>
        </p>
      ) : null}
      {/* Desde a 13.3, a terceira coluna (a partir de 1200 px) ou a gaveta é o painel "Dados do
          contato"; no celular ele ocupa a tela no lugar do chat. */}
      <div
        className="conversas"
        data-celular={ehCelular ? 'sim' : 'nao'}
        data-painel={painel === null || ehCelular ? 'nenhum' : modoDoPainel}
      >
        {ehCelular ? (
          mostrandoLista || conversaAberta === undefined ? (
            lista
          ) : (
            (painel ?? chat)
          )
        ) : (
          <>
            {lista}
            {chat}
            {painel}
          </>
        )}
      </div>
    </section>
  )
}
